import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
// Inline fallback UUID v4 generator (no external dependency)
const uuidv4 = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15));
import { Lock, Upload, AlertTriangle, Trash2, Clapperboard, LayoutGrid, FileText, Info, Maximize2, ImageOff, MoreVertical, ArrowLeft, RefreshCw } from "lucide-react";
import { getToolDraft, saveToolDraft } from "@/domain/toolDrafts";
import PageHeader, { Page } from "@/components/layout/PageHeader";
import { Notice, DemoBadge } from "@/components/common/States";
import MediaFrame, { resolveMediaUrl } from "@/components/common/MediaFrame";
import AssetPicker from "@/components/common/AssetPicker";
import PendingDialog from "@/components/common/PendingDialog";
import AssetDetailModal from "@/components/common/AssetDetailModal";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { getReadableAssetTitle, getTechnicalFilename } from "@/domain/assetDisplay";
import { downloadAsset } from "@/components/common/ImageActionBar";
import ConnectionModelPicker from "@/components/generator/ConnectionModelPicker";
import FormatPicker from "@/components/generator/FormatPicker";
import ReferenceChips from "@/components/generator/ReferenceChips";
import CostEstimate from "@/components/generator/CostEstimate";
import { PromptMentionEditor } from "@/components/common/PromptMentionEditor";
import { VideoPreflightModal } from "@/components/project/VideoPreflightModal";
import CharacterViewSelectorModal from "@/components/character/CharacterViewSelectorModal";
import ReferenceLimitModal from "@/components/generator/ReferenceLimitModal";
import NotFoundPage from "./NotFoundPage";
import { useStudio, useLookups, useCatalogModels } from "@/store/StudioContext";
import { useAuth } from "@/store/AuthContext";
import { connectionsForCapability, modelsFor, cheapestVideoCombo, isConnectionOperational, syncConnectionsWithCatalog } from "@/domain/connections";
import { QUANTITIES, IMAGE_RESOLUTIONS, VIDEO_RESOLUTIONS, formatMatchesAsset } from "@/domain/formats";
import { formatResolutionLabel, isKlingModel, isKlingPro } from "@/domain/resolutionLabels";
import {
  getModelSceneCapabilities,
  parseScenesFromPrompt,
  reconstructPromptWithScenes,
  compileScenesToTemporalPrompt,
  validateScenesForModel,
} from "@/domain/sceneEngine";
import { uploadAsset, api } from "@/lib/api";
import { isJobCompleted, isJobRunning, isJobFailed, getJobPrimaryAsset } from "@/domain/jobResults";
import { t } from "@/i18n";

const TOOL_KIND = { image: "image", video: "video", edit: "edit", upscale: "upscale" };

const Field = ({ label, hint, children }) => (
  <div><div className="mb-1.5 text-sm font-medium">{label}</div>{children}{hint && <p className="mt-1.5 text-xs text-[#a3a3a3]">{hint}</p>}</div>
);

// Sube el archivo al backend protegido (validación de tipo/tamaño en el
// servidor) y registra el recurso real devuelto, en vez de un blob local.
const uploadRealAsset = async (file, dispatch, kind = "image") => {
  const uploaded = await uploadAsset(file, file.name);
  dispatch({
    type: "ASSET_ADD",
    payload: { id: uploaded.id, type: kind, url: uploaded.url, source: "upload", label: file.name, isDemo: false },
  });
  return uploaded.id;
};

const QuickToolPage = () => {
  const { tool } = useParams();
  if (!TOOL_KIND[tool]) return <NotFoundPage />;
  return <ToolForm key={tool} tool={tool} />;
};

const ToolForm = ({ tool }) => {
  const [params] = useSearchParams();
  const { state, dispatch, catalog, catalogError, loadingCatalog, refreshCatalog, submitGeneration, submitUpscale, simulateJob } = useStudio();
  const { user } = useAuth();
  const isGuest = user?.role === "guest" || (user && user.id !== "owner" && user._id !== "owner" && user.role !== "owner");
  const { assetById } = useLookups();
  const prefs = state.onboarding.workspacePrefs || {};

  const currentUserId = user?.id || user?._id || "anonymous";
  const initialProjectId = params.get("project") || "";
  const initialDraft = useRef(getToolDraft(currentUserId, tool, initialProjectId) || {}).current;

  const [prompt, setPrompt] = useState(() => params.get("prompt") || initialDraft.prompt || "");
  const [projectId, setProjectId] = useState(() => initialProjectId || initialDraft.projectId || "");
  const [sourceId, setSourceId] = useState(() => params.get("asset") || initialDraft.sourceId || "");
  const [refs, setRefs] = useState(() => initialDraft.refs || []);
  const [refAssetIds, setRefAssetIds] = useState(() => initialDraft.refAssetIds || []);
  const [characterRefs, setCharacterRefs] = useState(() => initialDraft.characterRefs || []);
  const [activeCharacterModal, setActiveCharacterModal] = useState(null);
  const [limitResolution, setLimitResolution] = useState(null);
  const [pick, setPick] = useState(null);
  const [pending, setPending] = useState(false);
  const [pendingJobId, setPendingJobId] = useState(() => {
    try {
      return sessionStorage.getItem(`veyra_last_job_${tool}`) || null;
    } catch {
      return null;
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [viewerAsset, setViewerAsset] = useState(null);
  const [sourceDimensions, setSourceDimensions] = useState(null);
  const [sourceLoadError, setSourceLoadError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setSourceDimensions(null);
    setSourceLoadError(false);
  }, [sourceId]);

  useEffect(() => {
    if (pendingJobId) {
      try { sessionStorage.setItem(`veyra_last_job_${tool}`, pendingJobId); } catch {}
    }
  }, [pendingJobId, tool]);

  useEffect(() => {
    if (!catalog && !loadingCatalog && !catalogError) {
      refreshCatalog?.();
    }
  }, [catalog, loadingCatalog, catalogError, refreshCatalog]);

  const activeJob = pendingJobId
    ? state.jobs.find((j) => (j.backendJobId === pendingJobId || j.id === pendingJobId))
    : state.jobs.find((j) => {
        const targetKind = tool === "edit" ? "edit" : tool === "upscale" ? "upscale" : tool;
        return j.kind === targetKind && isJobCompleted(j);
      });
  const resultAsset = getJobPrimaryAsset(activeJob, assetById);

  const isImage = tool === "image", isVideo = tool === "video", isEdit = tool === "edit", isUpscale = tool === "upscale";
  // Edit y upscale usan modelos de imagen; upscale usa catálogo fal
  const kindCapability = isVideo ? "video" : isUpscale ? "upscale" : "image";

  // Catálogo del backend (fuente única de verdad para modelos y capacidades)
  const catalogRaw = useCatalogModels(kindCapability) ?? [];
  const catalogModels = catalogRaw.map(normalizeCatalogModel)
    .filter((m) => m.modelKind === kindCapability);
  const allConnections = syncConnectionsWithCatalog(state.connections, catalog);
  const eligibleConnections = connectionsForCapability(allConnections, kindCapability).filter(c => !c.isDemo);
  const [connectionId, setConnectionId] = useState(() => initialDraft.connectionId || "");
  const selectedConnection = eligibleConnections.find((c) => c.id === connectionId) || eligibleConnections.find((c) => c.status === "configured") || eligibleConnections[0] || null;
  const activeProvider = selectedConnection?.provider || null;

  // Fallback a modelos demo si el catálogo aún no cargó
  const allModels = useMemo(() => {
    return catalogModels.length > 0
      ? catalogModels
      : ((isImage || isVideo) ? modelsFor(kindCapability) : []);
  }, [catalogModels, isImage, isVideo, kindCapability]);

  const models = useMemo(() => {
    const base = allModels.filter((m) => {
      const mProv = m.provider_id || m.provider || (m.providerLabel === "Kie.ai" ? "kie" : m.providerLabel === "MuAPI" ? "muapi" : "apimart");
      return !activeProvider || mProv === activeProvider;
    });
    const modalityFiltered = tool === "edit" ? base.filter((m) => m.supports_editing) : base;
    return modalityFiltered.length > 0 ? modalityFiltered : allModels;
  }, [allModels, activeProvider, tool]);
  const hasConnection = allConnections.length > 0;
  const hasOperational = allConnections.some(isConnectionOperational);
  // El proveedor está operativo si el catálogo indica key_configured=true
  const anyKeyConfigured = Boolean(
    catalog?.providers?.some((p) => (isGuest ? p.provider_id === "apimart" : true) && p.key_configured) ||
    catalogRaw.some((m) => m.keyConfigured) ||
    eligibleConnections.some((c) => c.status === "configured")
  );
  const allowSimulate = catalog?.allow_simulate === true;
  // isPhase5 no bloquea vídeo: wan2.7 I2V está implementado en el backend.
  // Upscale de vídeo sigue pendiente pero se gestiona por separado.
  const isPhase5 = false;
  const budgetLimit = state.onboarding?.budget?.monthlyLimit ?? null;

  // ── Estado de upscale ──────────────────────────────────────────────────────
  const upscaleModels = catalogRaw.map(normalizeCatalogModel).filter((m) => m.modelKind === "upscale");
  const [upscaleModelId, setUpscaleModelId] = useState(() => initialDraft.upscaleModelId || upscaleModels[0]?.id || "");
  const upscaleModel = upscaleModels.find((m) => m.id === upscaleModelId) || upscaleModels[0] || null;
  const upscaleScales = upscaleModel?.upscaleScales || [2, 4];
  const [upscaleScale, setUpscaleScale] = useState(() => initialDraft.upscaleScale || upscaleScales[0] || 2);
  const [upscaleFormat, setUpscaleFormat] = useState(() => initialDraft.upscaleFormat || "jpeg");

  // Cuando los modelos de upscale cargan del catálogo, seleccionar el primero
  useEffect(() => {
    if (isUpscale && upscaleModels.length > 0 && !upscaleModelId) {
      setUpscaleModelId(upscaleModels[0].id);
    }
  }, [upscaleModels.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reserva conservadora para el modelo de upscale seleccionado
  const upscaleReserveMap = { "fal-ai/esrgan": 0.10, "fal-ai/topaz/upscale/image": 0.50 };
  const upscaleReserve = upscaleReserveMap[upscaleModelId] ?? 0.50;
  const upscaleFalConfigured = catalogRaw.some((m) => m.providerLabel === "fal.ai" && m.keyConfigured);

  // Presupuesto: null=cargando, true/false=cargado
  const [budgetConfigured, setBudgetConfigured] = useState(null);
  const [budgetAvailable, setBudgetAvailable] = useState(null);
  useEffect(() => {
    if (typeof api?.getBudgetStatus === "function") {
      try {
        const p = api.getBudgetStatus();
        if (p && typeof p.then === "function") {
          p.then((d) => {
            if (d != null) {
              setBudgetConfigured(d.configured ?? false);
              if (d.configured) setBudgetAvailable(d.available_usd ?? null);
            }
          }).catch(() => {});
        }
      } catch {}
    }
  }, []);

  // ── Kling 3: Autorización de un solo uso ────────────────────────────────────
  const [klingAuth, setKlingAuth] = useState(null);
  const [authorizingKling, setAuthorizingKling] = useState(false);

  const refreshKlingAuth = useCallback(async () => {
    try {
      if (typeof api.getKlingAuthStatus === "function") {
        const res = await api.getKlingAuthStatus();
        setKlingAuth(res);
      }
    } catch {
      setKlingAuth(null);
    }
  }, []);

  useEffect(() => {
    if (isVideo) {
      refreshKlingAuth();
    }
  }, [isVideo, refreshKlingAuth]);

  const handleAuthorizeKling = async () => {
    try {
      setAuthorizingKling(true);
      await api.authorizeKling();
      await refreshKlingAuth();
      toast.success("Autorización de un solo uso activada para Kling 3");
    } catch (e) {
      toast.error(e.message || "Error al emitir autorización de Kling 3");
    } finally {
      setAuthorizingKling(false);
    }
  };

  const requestedModel = params.get("model");
  const initialAvailable = requestedModel
    ? (models.find((m) => m.id === requestedModel) || models.find((m) => !m.pendingIntegration && !m.pricePending && m.generationAvailable !== false))
    : models.find((m) => !m.pendingIntegration && !m.pricePending && m.generationAvailable !== false);
  const [modelId, setModelId] = useState(() => (requestedModel ? initialAvailable?.id : (initialDraft.modelId || initialAvailable?.id || models[0]?.id || "")));
  const model = models.find((m) => m.id === modelId) || null;
  const prevModelId = useRef(modelId);

  const handleConnectionChange = useCallback((newConnId) => {
    setConnectionId(newConnId);
    const newConn = eligibleConnections.find((c) => c.id === newConnId);
    const newProvider = newConn?.provider || null;
    if (!newProvider) return;

    const nextModels = allModels.filter((m) => {
      const mProv = m.provider_id || m.provider || (m.providerLabel === "Kie.ai" ? "kie" : m.providerLabel === "MuAPI" ? "muapi" : "apimart");
      return mProv === newProvider && (tool !== "edit" || m.supports_editing);
    });

    if (!nextModels.length) return;

    const currentModelCompatible = nextModels.some(
      (m) => m.id === modelId && !m.pendingIntegration && !m.pricePending && m.generationAvailable !== false
    );
    if (!currentModelCompatible) {
      const firstAvailable = nextModels.find(
        (m) => !m.pendingIntegration && !m.pricePending && m.generationAvailable !== false
      );
      setModelId(firstAvailable?.id || nextModels[0]?.id || "");
    }
  }, [eligibleConnections, allModels, tool, modelId]);

  // Seleccionar automáticamente el primer modelo operativo si el seleccionado no está disponible
  useEffect(() => {
    if (!models.length) return;
    setModelId((curr) => {
      const currentModel = models.find((m) => m.id === curr);
      if (currentModel && !currentModel.pendingIntegration && !currentModel.pricePending && currentModel.generationAvailable !== false) {
        return curr;
      }
      const firstAvailable = models.find((m) => !m.pendingIntegration && !m.pricePending && m.generationAvailable !== false);
      return firstAvailable?.id || curr || models[0]?.id || "";
    });
  }, [models]);

  // Imagen: formato y resolución parten de las preferencias del Workspace o del borrador.
  const [format, setFormat] = useState(() => initialDraft.format || prefs.format || "auto");
  const [quantity, setQuantity] = useState(() => (typeof initialDraft.quantity === "number" ? initialDraft.quantity : 1));
  const [resolution, setResolution] = useState(() => initialDraft.resolution || prefs.imageResolution || IMAGE_RESOLUTIONS[0]);
  // Vídeo
  const initialStartId = tool === "video" ? (params.get("asset") || initialDraft.startId || "") : "";
  const [startId, setStartId] = useState(initialStartId);
  const [endId, setEndId] = useState(() => initialDraft.endId || "");
  const [motionVideoId, setMotionVideoId] = useState(() => initialDraft.motionVideoId || "");
  const [mode, setMode] = useState(() => {
    if (tool === "video" && params.get("mode")) {
      return params.get("mode");
    }
    if (tool === "video" && params.get("asset")) {
      return "image_to_video";
    }
    if (tool === "video" && initialDraft.mode) {
      return initialDraft.mode;
    }
    if (tool === "video" && initialStartId) {
      return "image_to_video";
    }
    if (model?.modes?.includes("image_to_video") && initialStartId) {
      return "image_to_video";
    }
    return model?.modes?.[0] || "text_to_video";
  });
  const [videoFormat, setVideoFormat] = useState(() => initialDraft.videoFormat || (models[0]?.formats?.includes(prefs.format) ? prefs.format : "16:9"));
  const [videoResolution, setVideoResolution] = useState(() => initialDraft.videoResolution || VIDEO_RESOLUTIONS[1]);
  const [duration, setDuration] = useState(() => initialDraft.duration || models[0]?.defaultDuration || models[0]?.durations?.[0] || 5);
  const [audioEnabled, setAudioEnabled] = useState(() => (initialDraft.audioEnabled != null ? initialDraft.audioEnabled : false));
  const [cheapNote, setCheapNote] = useState("");
  const [uploadTarget, setUploadTarget] = useState("source");
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [sourceMenuOpen, setSourceMenuOpen] = useState(false);
  const fileInputRef = useRef(null);

  // ── Motor Universal de Escenas Cinematográficas ──────────────────────────
  const sceneCaps = useMemo(() => getModelSceneCapabilities(model?.id), [model?.id]);
  const isSceneCompatible = sceneCaps.strategy !== "unsupported";
  const supportsMultishot = isSceneCompatible;

  const promptScenes = useMemo(() => parseScenesFromPrompt(prompt), [prompt]);
  const hasDetectedScenes = promptScenes.hasScenes;

  const [cinematicMode, setCinematicMode] = useState(() => {
    if (initialDraft.cinematicMode) return initialDraft.cinematicMode;
    try {
      return sessionStorage.getItem("veyra_cinematic_mode") || "freeform";
    } catch (_) {
      return "freeform";
    }
  });

  const [manualScenesEnabled, setManualScenesEnabled] = useState(() => Boolean(initialDraft.manualScenesEnabled));
  const multishotEnabled = (hasDetectedScenes || (cinematicMode === "visual" && manualScenesEnabled)) && isSceneCompatible;
  const [multishotConflict, setMultishotConflict] = useState(false);
  const [shots, setShots] = useState(() => (Array.isArray(initialDraft.shots) && initialDraft.shots.length > 0) ? initialDraft.shots : [
    { index: 1, prompt: "", duration: 3 },
    { index: 2, prompt: "", duration: 2 },
  ]);

  const activeScenes = hasDetectedScenes ? promptScenes.scenes : shots;
  const totalShotDuration = multishotEnabled
    ? activeScenes.reduce((acc, s) => acc + (Number(s.duration) || 0), 0)
    : duration;
  const shotDurationMismatch = multishotEnabled && (totalShotDuration !== duration);
  const maxCharsPerScene = sceneCaps?.maxPromptPerScene || 512;
  const shotPromptTooLong = multishotEnabled && activeScenes.some((s) => (s.prompt || "").length > maxCharsPerScene);
  const shotPromptEmpty = multishotEnabled && (activeScenes.length === 0 || activeScenes.some((s) => !(s.prompt || "").trim()));

  const handleSwitchCinematicMode = (newMode) => {
    setCinematicMode(newMode);
    try {
      sessionStorage.setItem("veyra_cinematic_mode", newMode);
    } catch (_) {}

    if (newMode === "visual") {
      setManualScenesEnabled(true);
      if (promptScenes.hasScenes) {
        setShots(promptScenes.scenes);
      } else if (prompt.trim()) {
        const half1 = Math.ceil(duration / 2);
        const half2 = Math.max(1, duration - half1);
        const template = [
          { index: 1, prompt: prompt.trim(), duration: half1 },
          { index: 2, prompt: "", duration: half2 },
        ];
        setShots(template);
      } else {
        const half1 = Math.ceil(duration / 2);
        const half2 = Math.max(1, duration - half1);
        setShots([
          { index: 1, prompt: "", duration: half1 },
          { index: 2, prompt: "", duration: half2 },
        ]);
      }
    } else if (newMode === "freeform") {
      if (shots.length > 0 && !hasDetectedScenes && manualScenesEnabled && shots.some((s) => s.prompt?.trim())) {
        const newPrompt = reconstructPromptWithScenes(promptScenes.preamble, shots);
        setPrompt(newPrompt);
      }
    }
  };

  // ── Redirección de herramientas no permitidas para invitados ─────────────
  useEffect(() => {
    if (isGuest && tool === "upscale") {
      navigate("/tools/image", { replace: true });
    }
  }, [isGuest, tool, navigate]);

  // ── Navegación directa y accesible ───────────────────────────────────────
  const handleBack = useCallback(() => {
    if (window.history.state && typeof window.history.state.idx === "number" && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/");
    }
  }, [navigate]);

  const allowedTools = useMemo(() => {
    const list = [
      { id: "image", label: t.tools?.navImage || "Imagen", path: "/tools/image" },
      { id: "edit", label: t.tools?.navEdit || "Editar imagen", path: "/tools/edit" },
      { id: "video", label: t.tools?.navVideo || "Vídeo", path: "/tools/video" },
    ];
    if (!isGuest) {
      list.push({ id: "upscale", label: t.tools?.navUpscale || "Upscaler", path: "/tools/upscale" });
    }
    return list;
  }, [isGuest]);

  // ── Cambio de proyecto y carga de su borrador ─────────────────────────────
  const handleProjectChange = useCallback((newProjId) => {
    setProjectId(newProjId);
    const projDraft = getToolDraft(currentUserId, tool, newProjId);
    if (projDraft) {
      if (projDraft.prompt != null) setPrompt(projDraft.prompt);
      if (projDraft.modelId) setModelId(projDraft.modelId);
      if (projDraft.connectionId) setConnectionId(projDraft.connectionId);
      if (tool === "image" || tool === "edit") {
        if (projDraft.format) setFormat(projDraft.format);
        if (projDraft.resolution) setResolution(projDraft.resolution);
        if (projDraft.quantity != null) setQuantity(projDraft.quantity);
        if (projDraft.refs) setRefs(projDraft.refs);
        if (projDraft.refAssetIds) setRefAssetIds(projDraft.refAssetIds);
        if (projDraft.characterRefs) setCharacterRefs(projDraft.characterRefs);
        if (tool === "edit" && projDraft.sourceId) setSourceId(projDraft.sourceId);
      }
      if (tool === "video") {
        if (projDraft.startId) setStartId(projDraft.startId);
        if (projDraft.endId) setEndId(projDraft.endId);
        if (projDraft.motionVideoId) setMotionVideoId(projDraft.motionVideoId);
        if (projDraft.mode) setMode(projDraft.mode);
        if (projDraft.videoFormat) setVideoFormat(projDraft.videoFormat);
        if (projDraft.videoResolution) setVideoResolution(projDraft.videoResolution);
        if (projDraft.duration) setDuration(projDraft.duration);
        if (projDraft.audioEnabled != null) setAudioEnabled(projDraft.audioEnabled);
        if (projDraft.shots) setShots(projDraft.shots);
      }
      if (tool === "upscale") {
        if (projDraft.sourceId) setSourceId(projDraft.sourceId);
        if (projDraft.upscaleModelId) setUpscaleModelId(projDraft.upscaleModelId);
        if (projDraft.upscaleScale) setUpscaleScale(projDraft.upscaleScale);
        if (projDraft.upscaleFormat) setUpscaleFormat(projDraft.upscaleFormat);
      }
    }
  }, [currentUserId, tool]);

  // ── Guardado automático de borradores aislados (por usuario y proyecto) ──
  useEffect(() => {
    if (!currentUserId) return;
    const draftData = {
      prompt,
      modelId,
      connectionId,
      projectId,
      ...(isImage ? { refs, refAssetIds, characterRefs, format, resolution, quantity } : {}),
      ...(isEdit ? { sourceId, refs, refAssetIds, characterRefs, format, resolution } : {}),
      ...(isVideo ? { startId, endId, motionVideoId, mode, videoFormat, videoResolution, duration, audioEnabled, cinematicMode, manualScenesEnabled, shots } : {}),
      ...(isUpscale ? { sourceId, upscaleModelId, upscaleScale, upscaleFormat } : {}),
    };
    saveToolDraft(currentUserId, tool, projectId, draftData);
  }, [
    currentUserId, tool, projectId, prompt, modelId, connectionId,
    isImage, isEdit, isVideo, isUpscale,
    refs, refAssetIds, characterRefs, format, resolution, quantity,
    sourceId, startId, endId, motionVideoId, mode, videoFormat, videoResolution,
    duration, audioEnabled, cinematicMode, manualScenesEnabled, shots,
    upscaleModelId, upscaleScale, upscaleFormat
  ]);

  const handleConvertPromptToScene1 = () => {
    if (!prompt.trim()) return;
    const half1 = Math.ceil(duration / 2);
    const half2 = Math.max(1, duration - half1);
    const template = [
      { index: 1, prompt: prompt.trim(), duration: half1 },
      { index: 2, prompt: "", duration: half2 },
    ];
    setShots(template);
    setPrompt(reconstructPromptWithScenes("", template));
    toast.success("Prompt convertido en Escena 1");
  };

  const handleToggleMultishot = () => {
    if (!multishotEnabled) {
      if (mode === "first_last_frame" || Boolean(endId)) {
        setMultishotConflict(true);
      } else {
        setManualScenesEnabled(true);
        setMultishotConflict(false);
        const half1 = Math.ceil(duration / 2);
        const half2 = Math.max(1, duration - half1);
        const template = [
          { index: 1, prompt: shots[0]?.prompt || "", duration: half1 },
          { index: 2, prompt: shots[1]?.prompt || "", duration: half2 },
        ];
        setShots(template);
        if (!hasDetectedScenes) {
          const generatedPrompt = reconstructPromptWithScenes(promptScenes.preamble, template);
          setPrompt(generatedPrompt);
        }
      }
    } else {
      setManualScenesEnabled(false);
      setMultishotConflict(false);
    }
  };

  const resolveConflictDisableEnd = () => {
    setMode(startId ? "image_to_video" : "text_to_video");
    setMultishotConflict(false);
    setManualScenesEnabled(true);
    const half1 = Math.ceil(duration / 2);
    const half2 = Math.max(1, duration - half1);
    const template = [
      { index: 1, prompt: shots[0]?.prompt || "", duration: half1 },
      { index: 2, prompt: shots[1]?.prompt || "", duration: half2 },
    ];
    setShots(template);
    if (!hasDetectedScenes) {
      setPrompt(reconstructPromptWithScenes(promptScenes.preamble, template));
    }
    toast.info("End Frame desactivado para permitir secuencia de escenas");
  };

  const addShot = () => {
    if (activeScenes.length >= (sceneCaps.maxScenes || 6)) return;
    const remaining = Math.max(1, duration - totalShotDuration);
    const updated = [
      ...activeScenes,
      { index: activeScenes.length + 1, prompt: "", duration: remaining },
    ];
    setShots(updated);
    setPrompt(reconstructPromptWithScenes(promptScenes.preamble, updated));
  };

  const deleteShot = (idx) => {
    if (activeScenes.length <= 1) return;
    const updated = activeScenes
      .filter((_, i) => i !== idx)
      .map((s, i) => ({ ...s, index: i + 1 }));
    setShots(updated);
    setPrompt(reconstructPromptWithScenes(promptScenes.preamble, updated));
  };

  const updateShotDuration = (idx, newDur) => {
    const val = Math.max(1, Math.min(sceneCaps.maxSceneDuration || 15, Number(newDur) || 1));
    const updated = activeScenes.map((s, i) => (i === idx ? { ...s, duration: val } : s));
    setShots(updated);
    setPrompt(reconstructPromptWithScenes(promptScenes.preamble, updated));
  };

  const updateShotPrompt = (idx, newPrompt) => {
    const updated = activeScenes.map((s, i) => (i === idx ? { ...s, prompt: newPrompt } : s));
    setShots(updated);
    setPrompt(reconstructPromptWithScenes(promptScenes.preamble, updated));
  };

  const autoAdjustLastShot = () => {
    if (!activeScenes.length) return;
    const sumOthers = activeScenes.slice(0, -1).reduce((acc, s) => acc + (Number(s.duration) || 0), 0);
    const needed = duration - sumOthers;
    if (needed >= 1) {
      const updated = activeScenes.map((s, i) => (i === activeScenes.length - 1 ? { ...s, duration: needed } : s));
      setShots(updated);
      setPrompt(reconstructPromptWithScenes(promptScenes.preamble, updated));
    } else {
      toast.warning("Reduce la duración de las escenas anteriores para ajustar el total");
    }
  };

  const applyScenesDurationToModel = () => {
    if (totalShotDuration > 0) {
      setDuration(totalShotDuration);
      toast.success(`Duración ajustada a ${totalShotDuration}s para coincidir con las escenas.`);
    }
  };


  const lastProcessedUrlRef = useRef({
    asset: params.get("asset") || "",
    mode: params.get("mode") || "",
    project: params.get("project") || "",
  });

  useEffect(() => {
    const currentAsset = params.get("asset") || "";
    const currentMode = params.get("mode") || "";
    const currentProject = params.get("project") || "";

    if (
      currentAsset !== lastProcessedUrlRef.current.asset ||
      currentMode !== lastProcessedUrlRef.current.mode ||
      currentProject !== lastProcessedUrlRef.current.project
    ) {
      lastProcessedUrlRef.current = {
        asset: currentAsset,
        mode: currentMode,
        project: currentProject,
      };

      if (isVideo) {
        if (currentAsset) {
          setStartId(currentAsset);
          setMode(currentMode || "image_to_video");
        }
      } else {
        if (currentAsset) setSourceId(currentAsset);
      }
      if (currentProject) setProjectId(currentProject);
    }
  }, [params, isVideo]);

  useEffect(() => {
    if (!eligibleConnections.length) return;
    if (requestedModel) {
      const targetModel = allModels.find((m) => m.id === requestedModel);
      if (targetModel) {
        const matchingConn = eligibleConnections.find(
          (c) => c.provider === (targetModel.provider_id || targetModel.provider)
        );
        if (matchingConn) {
          setConnectionId(matchingConn.id);
          return;
        }
      }
    }
    const preferredConn = eligibleConnections.find((c) => c.status === "configured") || eligibleConnections[0];
    setConnectionId((c) => (c && eligibleConnections.some((ec) => ec.id === c)) ? c : (preferredConn?.id || ""));
  }, [eligibleConnections, requestedModel, allModels]);

  useEffect(() => {
    if (prevModelId.current === modelId) return;
    prevModelId.current = modelId;
    if (!model) return;
    let changed = false;
    if (isImage || isEdit) {
      if (model.resolutions.length > 0 && !model.resolutions.includes(resolution)) {
        setResolution(model.resolutions[0]);
        changed = true;
      }
      const allFmts = [...(model.formats || []), ...(model.extendedFormats || [])];
      if (allFmts.length > 0 && !allFmts.includes(format)) {
        setFormat(allFmts[0]);
        changed = true;
      }
    } else if (isVideo) {
      if (model.resolutions.length > 0 && !model.resolutions.includes(videoResolution)) {
        setVideoResolution(model.resolutions[0]);
        changed = true;
      }
      if (model.formats.length > 0 && !model.formats.includes(videoFormat)) {
        setVideoFormat(model.formats[0]);
        changed = true;
      }
      if (!model.modes.includes("first_last_frame") && endId) {
        setEndId("");
      }
      if (!model.modes.includes(mode)) {
        // Preservar la intención I2V o FLF según los fotogramas seleccionados
        if (startId && endId && model.modes.includes("first_last_frame")) {
          setMode("first_last_frame");
        } else if (startId && model.modes.includes("image_to_video")) {
          setMode("image_to_video");
        } else if (model.modes.includes("image_to_video") && (mode === "first_last_frame" || mode === "image_to_video")) {
          setMode("image_to_video");
        } else {
          setMode(model.modes[0]);
        }
      } else if (startId && mode === "text_to_video" && model.modes.includes("image_to_video")) {
        // Evitar que el modo quede en text_to_video si ya hay una imagen cargada
        setMode(endId && model.modes.includes("first_last_frame") ? "first_last_frame" : "image_to_video");
      }
      if (model.audioSupported) {
        setAudioEnabled(Boolean(model.defaultAudio));
      } else {
        setAudioEnabled(false);
      }
      if (model.durations?.length > 0) {
        if (!model.durations.includes(duration)) {
          setDuration(model.defaultDuration || model.durations[0] || 4);
          changed = true;
        }
      }
      if (model.resolutions?.length > 0 && !model.resolutions.includes(videoResolution)) {
        setVideoResolution(model.defaultResolution || model.resolutions[0] || "720P");
        changed = true;
      }
      const targetSceneCaps = getModelSceneCapabilities(model.id);
      if (targetSceneCaps.strategy === "unsupported") {
        setManualScenesEnabled(false);
        setMultishotConflict(false);
      }
    }
    if (changed) toast.info(t.tools.modelChangedReset || t.tools.modelChangedWarning);
  }, [modelId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (models.length > 0 && (!modelId || !models.some((m) => m.id === modelId))) {
      setModelId(models[0].id);
    }
  }, [models.length, modelId]); // eslint-disable-line react-hooks/exhaustive-deps

  const needsSource = tool === "edit" || tool === "upscale";
  const source = assetById[sourceId];
  const readableSourceTitle = source ? getReadableAssetTitle({ asset: source, t }) : "";
  const technicalSourceFilename = source ? getTechnicalFilename(source) : "";
  const startAsset = assetById[startId];
  const endAsset = assetById[endId];
  const motionVideoAsset = assetById[motionVideoId];
  const cfg = t.tools[tool];

  const formatOk = isImage ? (model && (model.formats.includes(format) || model.extendedFormats.includes(format))) : (model && model.formats.includes(videoFormat));
  const resolutionOk = isImage ? Boolean(model?.resolutions.includes(resolution)) : Boolean(model?.resolutions.includes(videoResolution));
  const incompatible = (isImage || isVideo) && model && (!formatOk || !resolutionOk);
  const frameAsset = mode === "image_to_video" || mode === "first_last_frame" ? startAsset : null;
  const frameMismatch = isVideo && frameAsset && !formatMatchesAsset(videoFormat, frameAsset);
  const supportsFLF = isVideo && Boolean(model?.supportsFLF ?? model?.modes?.includes("first_last_frame"));

  // Límite de referencias desde el catálogo (VEYRA fase 3 = 2)
  const maxRefs = model?.veyra_max_references ?? model?.maxReferences ?? 2;
  const modelSupportsRefs = Boolean(model?.supports_references !== false && maxRefs > 0);

  // Unificar assetIds únicos entre referencias manuales, workspace y de personajes
  const allActiveRefAssetIds = useMemo(() => {
    const ids = new Set();
    characterRefs.forEach((cr) => { if (cr.assetId) ids.add(cr.assetId); });
    refAssetIds.forEach((id) => { if (id) ids.add(id); });
    refs.forEach((refId) => {
      const r = state.references?.find((x) => x.id === refId);
      if (r?.assetId) ids.add(r.assetId);
    });
    return Array.from(ids);
  }, [characterRefs, refAssetIds, refs, state.references]);

  const totalRefs = allActiveRefAssetIds.length;
  const refsAtMax = totalRefs >= maxRefs;
  // Sobrecoste por referencia adicional (NO VERIFICADO — importe $0.003 sin fuente primaria confirmada).
  // extraRefCost del modelo viene de estimated_cost_extra_ref_usd mapeado en normalizeCatalogModel.
  const extraRefCost = totalRefs > 1 ? (model?.extraRefCost ?? null) : null;

  // Incompatibilidad de referencias: modelo que no admite referencias o modalidad de vídeo con referencias generales
  const refsIncompatible = totalRefs > 0 && ((isImage || isEdit) ? !modelSupportsRefs : isVideo);
  const isKling = isVideo && (modelId === "kling-v3" || modelId?.startsWith("kling-"));
  const isKlingProMode = isKling && isKlingPro(videoResolution, model);

  const estimatedCost = (isImage || isEdit)
    ? (() => {
        if (!model) return null;
        const cpi = model.costPerImage || {};
        let resCost = null;
        if (resolution) {
          if (cpi[resolution] != null) {
            resCost = cpi[resolution];
          } else {
            const key = Object.keys(cpi).find((k) => k.toLowerCase() === resolution.toLowerCase());
            if (key && cpi[key] != null) resCost = cpi[key];
          }
        }
        if (resCost != null) return resCost;
        if (cpi.quality != null) return cpi.quality;
        if (cpi.default != null) return cpi.default;
        if (model.priceUsd != null) return model.priceUsd;
        if (model.reserveUsd != null) return model.reserveUsd;
        return null;
      })()
    : isVideo
      ? (
          isKlingProMode
            ? null // Tarifa PRO no confirmada por APIMart; evitamos mostrar un estimado engañoso
            : model?.ratePerSecondUsd
            ? Number((
                duration * (
                  model.ratePerSecondUsd + (audioEnabled && model.audioSupported ? (model.audioSurchargeRateUsd || 0) : 0)
                )
              ).toFixed(4))
            : (model?.priceUsd ?? model?.reserveUsd ?? null)
        )
      : null;

  const preventiveReserve = isVideo
    ? (
        isKlingProMode
          ? null // No disponible para PRO
          : model?.ratePerSecondUsd
          ? Number((
              duration * (
                model.ratePerSecondUsd + (audioEnabled && model.audioSupported ? (model.audioSurchargeRateUsd || 0) : 0)
              ) * (1 + (model.safetyMarginPct ?? 20) / 100)
            ).toFixed(4))
          : (model?.reserveUsd ?? null)
      )
    : null;

  const cost = estimatedCost;

  const klingAuthAvailable = Boolean(klingAuth?.authorized && !klingAuth?.consumed);

  const maxPromptChars = model?.maxPromptChars || (isKling ? 2500 : 5000);
  const promptTooLong = (prompt || "").length > maxPromptChars;
  const hasValidPrompt = multishotEnabled
    ? (!shotPromptEmpty && !shotPromptTooLong)
    : (prompt.trim().length > 0 && !promptTooLong);

  const isModelBlocked = Boolean(
    !model || model.pendingIntegration || model.pricePending || model.generationAvailable === false
  );

  const hasBudgetLimit = budgetConfigured === true || (budgetConfigured === null && budgetLimit != null);
  const isBudgetInsufficient = Boolean(isGuest && hasBudgetLimit && budgetAvailable != null && cost != null && budgetAvailable < cost);

  const canSubmit = catalogError || loadingCatalog || !hasConnection || isModelBlocked || refsIncompatible
    || (isGuest && (!anyKeyConfigured || !hasBudgetLimit || isBudgetInsufficient || isUpscale)) ? false
    : isUpscale ? Boolean(upscaleModel && source && upscaleFalConfigured && !isGuest)
    : tool === "edit" ? Boolean(source) && prompt.trim().length > 0 && !promptTooLong
    : isImage ? Boolean(connectionId && modelId && prompt.trim() && !promptTooLong && !incompatible)
    // Vídeo: prompt o tomas multishot válidas; dentro de límites; para I2V imagen inicial; para FLF ambas imágenes; multishot sin conflicto ni desfase de duración
    : isVideo ? Boolean(
        connectionId &&
        hasOperational &&
        modelId &&
        hasValidPrompt &&
        !incompatible &&
        !frameMismatch &&
        (!multishotEnabled || (!multishotConflict && !shotDurationMismatch && !shotPromptTooLong && !shotPromptEmpty && activeScenes.length >= 1 && activeScenes.length <= (sceneCaps.maxScenes || 6))) &&
        (mode === "image_to_video" ? Boolean(startAsset) : true) &&
        (mode !== "first_last_frame" || (Boolean(startAsset) && Boolean(endAsset) && !multishotEnabled)) &&
        (mode !== "motion_control" || (Boolean(startAsset) && Boolean(motionVideoAsset))) &&
        (!audioEnabled || !model?.audioIncompatibleWith?.includes(mode)) &&
        (!isKling || (klingAuthAvailable && (mode === "image_to_video" ? Boolean(startAsset) : mode === "first_last_frame" ? (Boolean(startAsset) && Boolean(endAsset)) : true)))
      )
    : prompt.trim().length > 0 && !promptTooLong;

  const submitButtonTitle = catalogError
    ? (t.connections?.connectionsError || "Error al consultar conexiones. Pulsa reintentar.")
    : loadingCatalog
    ? (t.connections?.loadingConnections || "Cargando conexiones...")
    : (isGuest && isUpscale)
    ? "El reescalado no está disponible para cuentas invitadas (requiere fal.ai)"
    : (isGuest && !anyKeyConfigured)
    ? "Configura tu clave de Apimart para generar"
    : (isGuest && !hasBudgetLimit)
    ? "Debes establecer un límite mensual antes de generar"
    : (isGuest && isBudgetInsufficient)
    ? "Presupuesto insuficiente para esta generación"
    : isPhase5
    ? t.tools.phase5Notice
    : refsIncompatible
    ? (isVideo ? (t.tools?.videoRefsIncompatible || "La modalidad de vídeo no admite imágenes de referencia") : (t.tools?.modelRefsIncompatible ? t.tools.modelRefsIncompatible(model?.label || modelId) : "El modelo no admite imágenes de referencia"))
    : promptTooLong
    ? `El prompt supera el límite de ${maxPromptChars} caracteres admitido por ${model?.label || model?.id || 'este modelo'}`
    : model?.pricePending
    ? "Precio pendiente — generación no disponible"
    : model?.pendingIntegration
    ? `${model.label}: integración pendiente`
    : !hasConnection
    ? (hasConnection ? t.connections.noneOperational : t.connections.required)
    : (tool === "edit" && !source)
    ? "Selecciona una imagen de origen para comenzar la edición"
    : (tool === "edit" && !prompt.trim())
    ? "Describe los cambios deseados para aplicar la edición"
    : (isKling && !klingAuthAvailable)
    ? "Kling 3 requiere autorización de un solo uso activa para generar vídeo"
    : (isKling && (!startAsset || mode !== "image_to_video"))
    ? "Kling 3 requiere una imagen inicial (modalidad I2V)"
    : (isVideo && mode === "image_to_video" && !startAsset)
    ? "Selecciona una imagen inicial válida para I2V"
    : (isVideo && !prompt.trim() && (!multishotEnabled || activeScenes.length === 0))
    ? "Introduce un prompt para generar vídeo"
    : undefined;

  const applyCheapTest = () => {
    if (!model) return;
    const combo = cheapestVideoCombo(model);
    setVideoResolution(combo.resolution); setDuration(combo.duration);
    setCheapNote(t.tools.cheapTestApplied(combo.resolution, combo.duration));
  };

  const handleOpenCharacterViews = useCallback((character) => {
    if (!character) return;
    setActiveCharacterModal(character);
  }, []);

  const handleConfirmCharacterViews = useCallback((chosenViews) => {
    if (!activeCharacterModal) return;

    if (!chosenViews || chosenViews.length === 0) {
      setCharacterRefs((prev) => prev.filter((cr) => cr.characterId !== activeCharacterModal.id));
      setActiveCharacterModal(null);
      return;
    }

    const otherCharRefs = characterRefs.filter((cr) => cr.characterId !== activeCharacterModal.id);

    const existingAssetIds = new Set([
      ...refAssetIds,
      ...refs.map((rId) => state.references?.find((r) => r.id === rId)?.assetId).filter(Boolean),
      ...otherCharRefs.map((cr) => cr.assetId),
    ]);

    const uniqueChosenViews = [];
    const seen = new Set();
    let hadDuplicate = false;

    for (const v of chosenViews) {
      if (existingAssetIds.has(v.assetId) || seen.has(v.assetId)) {
        hadDuplicate = true;
      } else {
        seen.add(v.assetId);
        uniqueChosenViews.push(v);
      }
    }

    const candidateCharRefs = [...otherCharRefs, ...uniqueChosenViews];
    const allCandidateAssetIds = Array.from(new Set([
      ...candidateCharRefs.map((c) => c.assetId),
      ...refAssetIds,
      ...refs.map((rId) => state.references?.find((r) => r.id === rId)?.assetId).filter(Boolean),
    ]));

    if (allCandidateAssetIds.length > maxRefs) {
      const currentItems = [
        ...characterRefs.map((cr) => ({
          assetId: cr.assetId,
          label: `@${cr.characterHandle} · ${cr.viewLabel || "Vista"}`,
          source: "character",
        })),
        ...refAssetIds.map((id) => ({
          assetId: id,
          label: assetById[id]?.label || id,
          source: "manual",
        })),
        ...refs.map((rId) => {
          const r = state.references?.find((x) => x.id === rId);
          return {
            assetId: r?.assetId,
            label: r?.label || rId,
            source: "workspace",
          };
        }).filter((x) => Boolean(x.assetId)),
      ];

      const candidateItems = uniqueChosenViews.map((v) => ({
        assetId: v.assetId,
        label: `@${v.characterHandle} · ${v.viewLabel || "Vista"}`,
        source: "candidate",
        viewData: v,
      }));

      setLimitResolution({
        currentReferences: currentItems,
        candidateReferences: candidateItems,
        candidateViews: uniqueChosenViews,
      });
      setActiveCharacterModal(null);
      return;
    }

    setCharacterRefs(candidateCharRefs);
    setActiveCharacterModal(null);
    if (hadDuplicate) {
      toast.info(t.tools?.duplicateRefIgnored || "Algunas vistas ya estaban seleccionadas como referencia");
    } else {
      toast.success(t.characters?.viewsAppliedToast || "Vistas vinculadas como referencia");
    }
  }, [activeCharacterModal, characterRefs, refAssetIds, refs, state.references, maxRefs, assetById]);

  const handleResolveLimit = useCallback((keptAssetIds) => {
    const keptSet = new Set(keptAssetIds);

    setRefAssetIds((prev) => prev.filter((id) => keptSet.has(id)));

    setRefs((prev) => prev.filter((rId) => {
      const r = state.references?.find((x) => x.id === rId);
      return r && keptSet.has(r.assetId);
    }));

    const candidateViews = limitResolution?.candidateViews || [];
    const combined = [...characterRefs, ...candidateViews];
    const seen = new Set();
    const nextCharRefs = [];

    for (const item of combined) {
      if (keptSet.has(item.assetId) && !seen.has(item.assetId)) {
        seen.add(item.assetId);
        nextCharRefs.push(item);
      }
    }

    setCharacterRefs(nextCharRefs);
    setLimitResolution(null);
    toast.success(t.tools?.refsLimitResolved || "Referencias actualizadas dentro del límite");
  }, [limitResolution, characterRefs, state.references]);

  const handleRemoveCharacterRef = useCallback((assetId) => {
    setCharacterRefs((prev) => prev.filter((cr) => cr.assetId !== assetId));
  }, []);

  const handleChangeCharacterRef = useCallback((cr) => {
    const char = (state.characters || []).find((c) => c.id === cr.characterId || c.handle === cr.characterHandle);
    if (char) {
      setActiveCharacterModal(char);
    }
  }, [state.characters]);

  const confirmVideoSubmit = async () => {
    if (refsIncompatible) {
      toast.error(t.tools?.videoRefsIncompatible || "La modalidad de vídeo no admite imágenes de referencia generales.");
      return;
    }
    setPreflightOpen(false);
    const selectedConn = allConnections.find((c) => c.id === connectionId) || allConnections.find(isConnectionOperational);
    const providerId = model?.provider_id || model?.providerId || model?.provider || selectedConn?.provider || "apimart";

    const isStructured = sceneCaps?.strategy === "structured";
    const finalPrompt = (multishotEnabled && !prompt.trim())
      ? activeScenes.map((s) => s.prompt.trim()).filter(Boolean).join(" | ")
      : prompt.trim();
    try {
      setSubmitting(true);
      const job = await submitGeneration({
        idempotency_key: uuidv4(),
        provider_id: providerId,
        model_id: modelId || "wan2.7",
        kind: "video",
        prompt: finalPrompt,
        source_asset_id: (mode === "image_to_video" || mode === "first_last_frame" || mode === "motion_control") ? (startId || null) : null,
        end_asset_id: (mode === "first_last_frame" && !multishotEnabled) ? (endId || null) : null,
        motion_video_asset_id: mode === "motion_control" ? (motionVideoId || null) : undefined,
        params: {
          video_mode: mode || "image_to_video",
          mode: (modelId === "kling-v3" || modelId?.startsWith("kling-")) ? (isKlingProMode ? "pro" : "std") : undefined,
          resolution: videoResolution,
          duration: totalShotDuration || duration,
          aspect_ratio: videoFormat,
          audio: audioEnabled && Boolean(model?.audioSupported) && !model?.audioIncompatibleWith?.includes(mode),
          end_asset_id: (mode === "first_last_frame" && !multishotEnabled) ? (endId || null) : undefined,
          motion_video_asset_id: mode === "motion_control" ? (motionVideoId || null) : undefined,
          multi_shot: (multishotEnabled && isStructured) ? true : undefined,
          shot_type: (multishotEnabled && isStructured) ? "customize" : undefined,
          multi_prompt: (multishotEnabled && isStructured)
            ? activeScenes.map((s, idx) => ({
                index: idx + 1,
                prompt: s.prompt.trim(),
                duration: Number(s.duration),
              }))
            : undefined,
          scenes: multishotEnabled
            ? activeScenes.map((s, idx) => ({
                index: idx + 1,
                prompt: s.prompt.trim(),
                duration: Number(s.duration),
              }))
            : undefined,
        },
        project_id: projectId || null,
      });
      setPendingJobId(job.job_id || null);
      toast.success(
        <span>
          {t.generation?.submittedToast || "Generación enviada."}{" "}
          {job.job_id && (
            <span className="font-mono text-xs opacity-75">
              (ID: {job.job_id.slice(0, 8)})
            </span>
          )}
        </span>
      );
    } catch (err) {
      if (err.message?.startsWith("budget_not_configured")) {
        toast.error(
          <span>
            {t.generation?.budgetNotConfigured || "No hay límite mensual de presupuesto configurado."}{" "}
            <a href="/settings#budget" className="underline font-medium ml-1" onClick={() => toast.dismiss()}>
              {t.generation?.budgetSettingsLink || "Ajustes › Presupuesto"}
            </a>
          </span>,
          { duration: 8000 }
        );
      } else {
        toast.error(err.message || t.generation?.submitError || "Error al enviar la generación de vídeo");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    const isEdit = tool === "edit";

    // Bloquear modelos no disponibles o con precio pendiente
    if (!isUpscale && model?.pendingIntegration) {
      toast.error(`${model?.label || modelId}: integración pendiente, no se puede generar.`);
      return;
    }
    if (!isUpscale && (model?.pricePending || model?.generationAvailable === false)) {
      toast.error(`${model?.label || modelId}: Precio pendiente — generación no disponible.`);
      return;
    }
    // ── Upscale (fal.ai) ───────────────────────────────────────────────────
    if (isUpscale) {
      if (!upscaleModel || !source) return;
      try {
        setSubmitting(true);
        const job = await submitUpscale({
          idempotency_key: uuidv4(),
          model_id: upscaleModel.id,
          source_asset_id: sourceId,
          scale: upscaleScale,
          output_format: upscaleFormat,
          project_id: projectId || null,
        });
        setPendingJobId(job.job_id || null);
        setPending(true);
      } catch (err) {
        if (err.message?.startsWith("budget_not_configured")) {
          toast.error(
            <span>
              {t.generation?.budgetNotConfigured || "No hay límite mensual de presupuesto configurado."}{" "}
              <a href="/settings#budget" className="underline font-medium ml-1" onClick={() => toast.dismiss()}>
                {t.generation?.budgetSettingsLink || "Ajustes › Presupuesto"}
              </a>
            </span>,
            { duration: 8000 }
          );
        } else {
          toast.error(err.message || t.generation?.submitError || "Error al enviar el upscale");
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // ── Vídeo (Apimart wan2.7 I2V via submitGeneration) ────────────────────
    // Abre el modal de confirmación preflight
    if (isVideo) {
      setPreflightOpen(true);
      return;
    }

    // Siempre usar backend para imagen y edición — con o sin clave.
    // Sin clave el backend crea una tarea "blocked" con job_id real, lo que permite:
    // (a) ver en panel de tareas, (b) usar "Simular finalización" para test funcional.
    const canUseBackend = Boolean(submitGeneration) && (isImage || isEdit);
    if (canUseBackend) {
      if (refsIncompatible) {
        toast.error(
          isVideo
            ? (t.tools?.videoRefsIncompatible || "La modalidad de vídeo no admite imágenes de referencia generales.")
            : (t.tools?.modelRefsIncompatible ? t.tools.modelRefsIncompatible(model?.label || modelId) : "El modelo no admite imágenes de referencia.")
        );
        return;
      }
      const selectedConn = allConnections.find((c) => c.id === connectionId) || allConnections.find(isConnectionOperational);
      const providerId = model?.provider_id || model?.providerId || model?.provider || selectedConn?.provider || "apimart";
      const refAssets = allActiveRefAssetIds.slice(0, maxRefs);
      try {
        setSubmitting(true);
        const job = await submitGeneration({
          idempotency_key: uuidv4(),
          provider_id: providerId,
          model_id: modelId || "seedream-5-0-pro",
          kind: isEdit ? "edit" : "image",
          prompt: prompt.trim(),
          reference_asset_ids: refAssets,
          source_asset_id: isEdit ? (sourceId || null) : null,
          params: { aspect_ratio: format, resolution, output_format: "jpeg" },
          project_id: projectId || null,
        });
        setPendingJobId(job.job_id || null);
        // Job real aceptado por backend: NO mostrar modal de "integración pendiente" (sería falso).
        toast.success(
          <span>
            {t.generation?.submittedToast || "Generación enviada."}{" "}
            <a href="/tasks" className="underline font-medium ml-1" onClick={() => toast.dismiss()}>
              {t.tasks?.title || "Ver en Tareas"}
            </a>
          </span>,
          { duration: 6000 }
        );
      } catch (err) {
        if (err.message?.startsWith("budget_not_configured")) {
          // Muestra mensaje con enlace directo a Ajustes > Presupuesto
          toast.error(
            <span>
              {t.generation?.budgetNotConfigured || "No hay límite mensual de presupuesto configurado."}{" "}
              <a href="/settings#budget" className="underline font-medium ml-1" onClick={() => toast.dismiss()}>
                {t.generation?.budgetSettingsLink || "Ajustes › Presupuesto"}
              </a>
            </span>,
            { duration: 8000 }
          );
        } else {
          if (err.message?.startsWith("provider_error:")) {
            let providerMsg = err.message.replace(/^provider_error:\s*/, "");
            const m = err.message.match(/"message"\s*:\s*"([^"]+)"/);
            if (m) providerMsg = m[1];
            toast.error(providerMsg, { duration: 8000 });
          } else if (err.message?.startsWith("budget_insufficient")) {
            const msg = err.message.replace("budget_insufficient: ", "") || t.generation?.budgetInsufficient || "Presupuesto insuficiente";
            toast.error(msg);
          } else {
            toast.error(t.generation?.submitError || "Error al enviar la generación");
          }
        }
      } finally {
        setSubmitting(false);
      }
    } else {
      // Vídeo y otras herramientas sin backend en Fase 3 — tarea local bloqueada
      const common = { projectId: projectId || null, connectionId: connectionId || null, model: modelId || null, status: "blocked", note: t.tasks.blockedReasonNoKey, referenceIds: refs, costEstimate: cost };
      if (isVideo) dispatch({ type: "JOB_CREATE", payload: { ...common, kind: "video", prompt: prompt.trim(), params: { mode, format: videoFormat, resolution: videoResolution, duration, startAssetId: startId || null, endAssetId: endId || null, referenceAssetIds: refAssetIds } } });
      else dispatch({ type: "JOB_CREATE", payload: { kind: TOOL_KIND[tool], projectId: projectId || null, prompt: prompt.trim(), params: { sourceAssetId: sourceId || null }, referenceIds: refs, status: "blocked", note: t.tasks.blockedReasonNoKey } });
      setPending(true);
    }
  };

  const [uploadBusy, setUploadBusy] = useState(false);

  const onUploadFile = async (e, target) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    // Para refs: subir hasta las que quepan en el límite; para otros targets: solo la primera.
    const filesToProcess = target === "ref"
      ? files.slice(0, Math.max(0, maxRefs - totalRefs))
      : files.slice(0, 1);
    if (!filesToProcess.length) return;
    setUploadBusy(true);
    try {
      const ids = [];
      for (const file of filesToProcess) {
        const id = await uploadRealAsset(file, dispatch, target === "motion" ? "video" : "image");
        ids.push(id);
      }
      if (target === "source") setSourceId(ids[0]);
      else if (target === "start") {
        setStartId(ids[0]);
        if (model?.modes?.includes("image_to_video") && mode === "text_to_video") {
          setMode(endId && model?.modes?.includes("first_last_frame") ? "first_last_frame" : "image_to_video");
        }
      }
      else if (target === "end") {
        setEndId(ids[0]);
        if (model?.modes?.includes("first_last_frame")) setMode("first_last_frame");
      }
      else if (target === "motion") setMotionVideoId(ids[0]);
      else if (target === "ref") setRefAssetIds((x) => [...x, ...ids]);
    } catch (err) {
      const msg =
        err.message === "file_too_large" ? t.tools.uploadTooLarge
        : err.message === "unsupported_file_type" || err.message === "invalid_image" ? t.tools.uploadUnsupported
        : err.message === "empty_file" ? t.tools.uploadEmpty
        : t.tools.uploadFailed;
      toast.error(msg);
    } finally {
      setUploadBusy(false);
    }
  };
  const openUpload = (target) => { setUploadTarget(target); fileInputRef.current?.click(); };

  const renderInlineResult = () => (
    (activeJob || resultAsset) ? (
      <div data-testid="tool-inline-result" className="rounded-xl border border-[#303030] bg-[#141414] p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#d8d4cd]">
            {t.generation?.resultTitle || "Resultado de generación"}
          </span>
          {isJobCompleted(activeJob) && (
            <span className="rounded bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-400">
              {t.generation?.completedStatus || "Completado"}
            </span>
          )}
          {isJobRunning(activeJob) && (
            <span className="inline-flex items-center gap-1.5 rounded bg-[#f5b544]/10 px-2 py-0.5 text-[11px] font-medium text-[#f5b544]">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#f5b544]" />
              {t.generation?.runningStatus || "Generando..."}
            </span>
          )}
          {isJobFailed(activeJob) && (
            <span className="rounded bg-red-500/10 px-2 py-0.5 text-[11px] font-medium text-red-400">
              {t.generation?.failedStatus || "Fallido"}
            </span>
          )}
        </div>

        {resultAsset ? (
          <div className="flex flex-col gap-3">
            <div
              className="relative group cursor-pointer overflow-hidden rounded-lg border border-[#262626] bg-black/40 hover:border-[#404040] transition-colors"
              onClick={() => setViewerAsset(resultAsset)}
              title="Pulsar imagen para ampliar en visor superpuesto"
              data-testid="tool-result-media-container"
            >
              <MediaFrame
                asset={resultAsset}
                className="aspect-video w-full transition-transform duration-200 group-hover:scale-[1.01]"
                fit="contain"
                controls={false}
                testId="tool-result-media"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setViewerAsset(resultAsset);
                }}
                className="absolute top-2.5 right-2.5 flex items-center gap-1.5 rounded-md bg-[#121212]/90 px-2.5 py-1.5 text-xs font-medium text-[#f4f1ec] backdrop-blur border border-[#383838] shadow-lg opacity-90 group-hover:opacity-100 hover:bg-[#1f1f1f] hover:border-[#f5b544] hover:text-[#f5b544] transition-all"
                data-testid="tool-result-expand-btn"
                title="Ampliar imagen"
              >
                <Maximize2 size={13} className="text-[#f5b544]" />
                <span>Ampliar</span>
              </button>
            </div>
            <div className="flex items-center justify-between text-xs text-[#a3a3a3]">
              <span className="truncate max-w-[260px]">{getReadableAssetTitle({ asset: resultAsset, t }) || activeJob?.prompt || t.common.untitled}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setViewerAsset(resultAsset)}
                  className="v-btn-ghost v-btn-sm text-[#f5b544] flex items-center gap-1.5"
                  data-testid="tool-result-action-expand"
                >
                  <Maximize2 size={12} />
                  <span>Ampliar</span>
                </button>
                {resultAsset.type === "image" && (projectId || resultAsset.projectId) && (
                  <Link
                    to={`/projects/${projectId || resultAsset.projectId}/editor?asset=${resultAsset.id}`}
                    className="v-btn-ghost v-btn-sm text-[#d8d4cd]"
                  >
                    {t.storyboard?.openInEditor || "Abrir en Editor"}
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => downloadAsset(resultAsset)}
                  className="v-btn-ghost v-btn-sm"
                >
                  {t.gallery?.download || "Descargar"}
                </button>
              </div>
            </div>
          </div>
        ) : isJobRunning(activeJob) ? (
          <div className="flex min-h-[140px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-[#303030] bg-[#101010] p-6 text-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#303030] border-t-[#f5b544]" />
            <p className="text-xs text-[#a3a3a3]">{t.generation?.generating || "Procesando generación..."}</p>
          </div>
        ) : isJobFailed(activeJob) ? (
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-400">
            {activeJob?.errorDetail || t.generation?.failedToast || "La tarea no pudo completarse."}
          </div>
        ) : null}
      </div>
    ) : null
  );

  const renderSourcePreview = () => (
    <div data-testid="tool-source-container" className="flex flex-col gap-3">
      {/* Área centrada adaptada a las dimensiones intrínsecas reales de la imagen */}
      <div className="flex w-full items-center justify-center">
        {source ? (
          sourceLoadError ? (
            <div
              data-testid="tool-source-error"
              className="flex h-28 w-full max-w-sm flex-col items-center justify-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/5 p-4 text-center text-xs text-red-400"
            >
              <ImageOff size={18} className="opacity-60" />
              <span>{t.tools.uploadFailed || "No se pudo cargar la imagen de origen."}</span>
            </div>
          ) : (
            <div
              data-testid="tool-source-preview-box"
              className="group relative inline-flex max-w-full items-center justify-center overflow-hidden rounded-lg border border-[#262626] bg-[#0c0c0c] transition-all hover:border-[#f5b544]/60 cursor-pointer shadow-sm"
              onClick={() => setViewerAsset(source)}
              title={t.tools.clickToExpand || "Pulsar para ampliar"}
            >
              <img
                src={resolveMediaUrl(source.url)}
                alt={readableSourceTitle || source.filename || t.tools.sourceImage}
                onLoad={(e) => {
                  const nw = e.currentTarget.naturalWidth;
                  const nh = e.currentTarget.naturalHeight;
                  if (nw && nh) {
                    setSourceDimensions({ width: nw, height: nh });
                  }
                }}
                onError={() => setSourceLoadError(true)}
                style={{
                  maxWidth: "100%",
                  maxHeight: "min(340px, 38vh)",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                }}
                className="block select-none rounded object-contain"
                draggable={false}
              />

              {/* Overlay sutil al hacer hover para invitar a ampliar */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 pointer-events-none">
                <span className="flex items-center gap-1.5 rounded-full bg-[#141414]/90 px-3 py-1.5 text-xs font-medium text-[#f5b544] shadow-lg border border-[#f5b544]/30 backdrop-blur-sm">
                  <Maximize2 size={13} />
                  <span>{t.tools.expand || "Ampliar"}</span>
                </span>
              </div>

              {source.isDemo && (
                <DemoBadge className="absolute left-2 top-2 bg-[#101010]/80 backdrop-blur" />
              )}
            </div>
          )
        ) : (
          <div
            data-testid="tool-source-empty"
            className="flex h-36 w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-[#303030] bg-[#101010] p-4 text-center text-[#737373]"
          >
            <ImageOff size={22} className="opacity-40" />
            <span className="text-xs font-medium text-[#a3a3a3]">
              {t.tools.noSourceSelected || "No hay imagen de origen seleccionada"}
            </span>
            <span className="text-[11px] text-[#666] max-w-xs leading-relaxed">
              {t.tools.selectSourceHelp || "Elige una imagen de la galería o sube una desde tu equipo para comenzar la edición."}
            </span>
            <div className="mt-1 flex items-center gap-2">
              <button
                type="button"
                data-testid="tool-pick-source"
                className="v-btn-ghost v-btn-sm"
                onClick={() => setPick("source")}
              >
                {t.tools.pickSource || "Elegir de la galería"}
              </button>
              <button
                type="button"
                data-testid="tool-upload-source"
                className="v-btn-ghost v-btn-sm"
                disabled={uploadBusy}
                onClick={() => openUpload("source")}
              >
                <Upload size={12} />
                {uploadBusy ? t.common.loading : (t.tools.uploadTest || "Subir imagen")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Dimensiones y nombre legibles, centrados con la imagen */}
      {source && !sourceLoadError && (
        <div className="flex flex-col items-center gap-1 text-center px-2">
          <span
            className="truncate max-w-full font-medium text-xs text-[#d8d4cd]"
            title={technicalSourceFilename || readableSourceTitle}
          >
            {readableSourceTitle}
          </span>
          <span data-testid="tool-source-dimensions" className="font-mono text-[11px] text-[#888]">
            {source.width && source.height
              ? `${source.width}×${source.height}`
              : sourceDimensions
              ? `${sourceDimensions.width}×${sourceDimensions.height}`
              : ""}
          </span>
        </div>
      )}

      {/* Barra de acciones */}
      {source && (
        <div className="flex items-center justify-center gap-2 pt-0.5">
          <button
            type="button"
            data-testid="tool-pick-source"
            className="v-btn-ghost v-btn-sm h-8 px-3 text-xs"
            onClick={() => setPick("source")}
          >
            {t.tools.replaceSource || "Reemplazar"}
          </button>
          {!sourceLoadError && (
            <button
              type="button"
              data-testid="tool-source-action-expand"
              className="v-btn-ghost v-btn-sm h-8 px-3 text-xs text-[#f5b544] flex items-center gap-1.5"
              onClick={() => setViewerAsset(source)}
              title={t.tools.expand || "Ampliar"}
            >
              <Maximize2 size={13} />
              <span>{t.tools.expand || "Ampliar"}</span>
            </button>
          )}

          <DropdownMenu open={sourceMenuOpen} onOpenChange={setSourceMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="tool-source-more-btn"
                aria-label={t.tools.moreActions || "Más acciones"}
                title={t.tools.moreActions || "Más acciones"}
                onClick={() => setSourceMenuOpen((v) => !v)}
                className="v-btn-ghost v-btn-sm h-8 w-8 p-0 flex items-center justify-center text-[#a3a3a3] hover:text-[#f4f1ec]"
              >
                <MoreVertical size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={4}
              className="w-48 border border-[#2a2a2a] bg-[#141414] p-1 text-xs shadow-xl backdrop-blur"
            >
              <DropdownMenuItem
                data-testid="tool-upload-source"
                onClick={() => {
                  setSourceMenuOpen(false);
                  openUpload("source");
                }}
                className="flex items-center gap-2 px-2.5 py-1.5 cursor-pointer text-[#d8d4cd] hover:text-[#f5b544] hover:bg-[#202020] rounded"
              >
                <Upload size={13} />
                <span>{t.tools.uploadAnother || "Subir otra imagen"}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="tool-remove-source"
                onClick={() => {
                  setSourceMenuOpen(false);
                  setSourceId("");
                }}
                className="flex items-center gap-2 px-2.5 py-1.5 cursor-pointer text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
              >
                <Trash2 size={13} />
                <span>{t.tools.removeSource || "Quitar"}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );

  return (
    <Page testId={`tool-page-${tool}`}>
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          data-testid="tool-back-btn"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[#a3a3a3] hover:text-[#f4f1ec] transition-colors focus:outline-none focus:ring-1 focus:ring-[#f5b544] px-2 py-1 -ml-2 rounded"
          aria-label={t.tools?.back || "Volver"}
        >
          <ArrowLeft size={14} className="shrink-0" aria-hidden="true" />
          <span>{t.tools?.back || "Volver"}</span>
        </button>
      </div>

      <PageHeader
        eyebrow={t.home?.quick || "Herramientas rápidas"}
        title={cfg?.title || "Herramienta"}
        right={
          <nav
            data-testid="tool-navigation-bar"
            aria-label="Herramientas rápidas"
            className="flex flex-wrap items-center gap-1.5 rounded-lg border border-[#262626] bg-[#141414] p-1"
          >
            {allowedTools.map((tItem) => {
              const isActive = tool === tItem.id;
              return (
                <Link
                  key={tItem.id}
                  to={tItem.path}
                  data-testid={`tool-nav-${tItem.id}`}
                  aria-current={isActive ? "page" : undefined}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all whitespace-nowrap ${
                    isActive
                      ? "bg-[#252525] text-[#f5b544] shadow-sm font-semibold border border-[#383838]"
                      : "text-[#a3a3a3] hover:text-[#f4f1ec] hover:bg-[#1c1c1c]"
                  }`}
                >
                  {tItem.label}
                </Link>
              );
            })}
          </nav>
        }
      />

      {/* ── AVISOS SEGÚN ESTADO REAL ── */}
      <div className="mb-6 flex flex-col gap-3">
        {catalogError && (
          <Notice tone="error" testId="catalog-query-error-banner">
            <div className="flex items-center justify-between gap-4 w-full">
              <span>{t.connections?.catalogLoadError || "Error al consultar el catálogo de proveedores y modelos."}</span>
              <button
                type="button"
                onClick={() => refreshCatalog?.()}
                className="px-2.5 py-1 text-xs rounded bg-red-900/40 hover:bg-red-900/60 text-white font-medium inline-flex items-center gap-1.5 transition-colors"
                data-testid="catalog-error-retry-btn"
              >
                <RefreshCw size={13} className={loadingCatalog ? "animate-spin" : ""} />
                {t.connections?.catalogErrorRetry || "Reintentar"}
              </button>
            </div>
          </Notice>
        )}
        {isGuest ? (
          <>
            {!catalogError && !anyKeyConfigured && (
              <Notice tone="warn" testId="guest-key-missing-notice">
                {t.tools?.guestKeyMissing || "Configura tu clave de Apimart para generar."}{" "}
                <Link to="/settings#connections" className="underline font-medium ml-1">
                  {t.nav?.settings || "Ajustes"} → {t.settings?.connections || "Conexiones"}
                </Link>
              </Notice>
            )}
            {!catalogError && anyKeyConfigured && !hasBudgetLimit && (
              <Notice tone="warn" testId="guest-budget-missing-notice">
                {t.tools?.guestBudgetMissing || "Establece un límite mensual antes de generar."}{" "}
                <Link to="/settings#connections" className="underline font-medium ml-1">
                  {t.nav?.settings || "Ajustes"} → {t.settings?.connections || "Conexiones"}
                </Link>
              </Notice>
            )}
            {!catalogError && anyKeyConfigured && hasBudgetLimit && isBudgetInsufficient && (
              <Notice tone="warn" testId="guest-budget-insufficient-notice">
                {t.tools?.budgetInsufficient
                  ? t.tools.budgetInsufficient(budgetAvailable.toFixed(2), cost.toFixed(2))
                  : `Presupuesto insuficiente: el disponible ($${budgetAvailable.toFixed(2)}) no cubre el coste estimado ($${cost.toFixed(2)}).`}
              </Notice>
            )}
          </>
        ) : (
          <>
            {!catalogError && !anyKeyConfigured && (
              <Notice tone="warn" testId="tool-provider-notice">
                {hasConnection ? t.connections.noneOperational : t.connections.required}{" "}
                <Link to="/settings#connections" className="underline font-medium ml-1">
                  {t.nav?.settings || "Ajustes"} → {t.settings?.connections || "Conexiones"}
                </Link>
              </Notice>
            )}
          </>
        )}
        {isModelBlocked && (
          <Notice tone="warn" testId="tool-model-blocked-notice">
            {model?.pendingIntegration
              ? `${model?.label || modelId}: integración pendiente`
              : model?.pricePending
              ? `${model?.label || modelId}: precio pendiente — generación no disponible`
              : `${model?.label || modelId}: modelo no disponible`}
          </Notice>
        )}
      </div>
      {isEdit ? (
        <div data-testid="tool-edit-workspace" className="mt-8 flex flex-col gap-6">
          {renderInlineResult()}

          <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 gap-6 items-start">
            {/* ── COLUMNA 1 (Izquierda): Imagen de origen y acciones ── */}
            <div className="flex flex-col gap-4">
              <div className="v-panel p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between border-b border-[#222] pb-2.5">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#d8d4cd]">
                    {t.tools.sourceImage || "Imagen de origen"}
                  </span>
                  <span className="text-[11px] text-[#737373]">
                    {t.tools.originalPreservedBrief || "El original se conserva"}
                  </span>
                </div>
                {renderSourcePreview()}
              </div>
            </div>

            {/* ── COLUMNA 2 (Centro): Instrucciones de edición y Referencias adicionales ── */}
            <div className="flex flex-col gap-4">
              <div className="v-panel p-4 flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="tool-prompt-edit" className="text-sm font-medium text-[#ededed]">
                      {t.tools.whatToChange || "¿Qué quieres cambiar?"}
                    </label>
                    <span className="text-[11px] text-[#737373]">
                      {(prompt || "").length} / {maxPromptChars}
                    </span>
                  </div>
                  <PromptMentionEditor
                    value={prompt}
                    onChange={setPrompt}
                    placeholder={t.editor.instructionsPh || "Describe los cambios, adiciones o ajustes sobre la imagen original..."}
                    rows={5}
                    testId="tool-prompt"
                    characters={state.characters || []}
                    characterReferences={characterRefs}
                    assetById={assetById}
                    onSelectCharacterMention={handleOpenCharacterViews}
                    disabled={submitting}
                    maxChars={maxPromptChars}
                    modelLabel={model?.label || model?.id}
                  />
                </div>

                <div className="border-t border-[#222]" />

                <div className="flex flex-col gap-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#ededed]">
                      {t.tools.additionalReferences || "Referencias adicionales"}
                    </span>
                    <span className="text-[11px] text-[#737373]">
                      máx. {maxRefs}
                    </span>
                  </div>
                  <p className="text-[11px] text-[#888]">
                    {t.tools.additionalReferencesNote || "Añade imágenes de referencia para guiar personajes, estilo o elementos."}
                  </p>

                  <ReferenceChips
                    references={state.references}
                    selectedIds={refs}
                    assetById={assetById}
                    onToggle={(id) => {
                      const refAssetId = state.references?.find((r) => r.id === id)?.assetId;
                      if (!refs.includes(id)) {
                        if (refsAtMax) {
                          toast.warning(`Máximo ${maxRefs} referencias en esta fase`);
                          return;
                        }
                        if (refAssetId && allActiveRefAssetIds.includes(refAssetId)) {
                          toast.info("Esta referencia ya está seleccionada");
                          return;
                        }
                      }
                      setRefs((x) => (x.includes(id) ? x.filter((i) => i !== id) : [...x, id]));
                    }}
                    extraAssets={refAssetIds.map((id) => assetById[id]).filter(Boolean)}
                    onRemoveExtra={(id) => setRefAssetIds((x) => x.filter((i) => i !== id))}
                    characterReferences={characterRefs}
                    onRemoveCharacterRef={handleRemoveCharacterRef}
                    onChangeCharacterRef={handleChangeCharacterRef}
                  />

                  {refsAtMax && (
                    <p className="text-xs text-[#f5b544]">
                      {`Límite de ${maxRefs} referencias alcanzado`}
                    </p>
                  )}

                  {refsIncompatible && (
                    <div className="mt-2" data-testid="refs-incompatible-notice">
                      <Notice tone="warn">
                        {isVideo
                          ? (t.tools?.videoRefsIncompatible || "La modalidad de vídeo no admite imágenes de referencia generales.")
                          : (t.tools?.modelRefsIncompatible ? t.tools.modelRefsIncompatible(model?.label || modelId) : "El modelo seleccionado no admite imágenes de referencia.")}
                      </Notice>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-0.5">
                    <button
                      type="button"
                      data-testid="tool-pick-ref-edit"
                      className="v-btn-ghost v-btn-sm"
                      disabled={refsAtMax}
                      onClick={() => setPick("ref")}
                    >
                      {t.tools.pickSource || "Elegir de la galería"}
                    </button>
                    <button
                      type="button"
                      data-testid="tool-upload-ref-edit"
                      className="v-btn-ghost v-btn-sm"
                      disabled={uploadBusy || refsAtMax}
                      onClick={() => openUpload("ref")}
                    >
                      <Upload size={12} />
                      {uploadBusy ? t.common.loading : (t.tools.uploadTest || "Subir imagen")}
                    </button>
                  </div>

                  <p className="text-[11px] text-[#6b6b6b] pt-1">
                    {t.tools.originalPreservedFull || "El original se conserva; la edición crea un activo nuevo."}
                  </p>
                </div>
              </div>
            </div>

            {/* ── COLUMNA 3 (Derecha): Proveedor, Modelo, Formato, Resolución, Proyecto, Coste y Botón ── */}
            <div className="flex flex-col gap-4">
              <div className="v-panel p-4 flex flex-col gap-3.5">
                <ConnectionModelPicker
                  connections={eligibleConnections}
                  connectionId={connectionId}
                  onConnection={handleConnectionChange}
                  models={models}
                  modelId={modelId}
                  onModel={setModelId}
                  testPrefix="tool"
                  loading={loadingCatalog}
                  error={Boolean(catalogError)}
                />

                {model?.pricePending && (
                  <div data-testid="tool-price-pending-notice" className="rounded border border-[#f5b544]/40 bg-[#f5b544]/10 p-2.5 text-xs text-[#f5b544]">
                    <span className="font-semibold">Precio pendiente — generación no disponible:</span> Este modelo está técnicamente integrado, pero su tarifa aún no ha sido verificada.
                  </div>
                )}

                <Field label={t.tools.format}>
                  <FormatPicker
                    formats={model?.formats || []}
                    extendedFormats={model?.extendedFormats || []}
                    value={format}
                    onChange={setFormat}
                    testPrefix="tool-format"
                  />
                </Field>

                <Field label={t.tools.resolution}>
                  {(() => {
                    const allRes = IMAGE_RESOLUTIONS;
                    const supported = model?.resolutions?.length > 0 ? model.resolutions : allRes;
                    const showPills = supported.length > 1 && supported.length <= 4;
                    return (
                      <div className="flex flex-col gap-2">
                        {showPills && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="tool-resolution-pills">
                            {supported.map((r) => {
                              const label = formatResolutionLabel(r, model);
                              const isSelected = resolution === r;
                              return (
                                <button
                                  key={r}
                                  type="button"
                                  data-testid={`resolution-pill-${r}`}
                                  onClick={() => setResolution(r)}
                                  className={`flex items-center justify-between px-3 py-2 rounded-lg border text-xs transition-all ${
                                    isSelected
                                      ? "border-[#f5b544] bg-[#f5b544]/10 text-[#f5b544] font-semibold ring-1 ring-[#f5b544]/40"
                                      : "border-[#2a2a2a] bg-[#141414] text-[#a3a3a3] hover:border-[#404040] hover:text-[#ededed]"
                                  }`}
                                >
                                  <span className="truncate">{label}</span>
                                  {isSelected ? (
                                    <span className="h-2 w-2 rounded-full bg-[#f5b544] shrink-0" />
                                  ) : (
                                    <span className="h-2 w-2 rounded-full border border-[#444] shrink-0" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        <select
                          data-testid="tool-resolution-select"
                          className={`v-input ${showPills ? "hidden" : ""}`}
                          value={resolution}
                          disabled={supported.length <= 1}
                          onChange={(e) => setResolution(e.target.value)}
                          aria-label={t.tools.resolution}
                        >
                          {supported.map((r) => (
                            <option key={r} value={r}>
                              {formatResolutionLabel(r, model)}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                </Field>

                <Field label={t.tools.project} hint={t.common.noProjectHint}>
                  <select
                    data-testid="tool-project"
                    className="v-input"
                    value={projectId}
                    onChange={(e) => handleProjectChange(e.target.value)}
                  >
                    <option value="">{t.common.noProject}</option>
                    {state.projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </Field>

                <div className="border-t border-[#222] my-0.5" />

                {catalogError && (
                  <div data-testid="catalog-error-side-banner" className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-red-400">{t.connections?.catalogLoadError || "Error al consultar conexiones."}</span>
                      <button
                        type="button"
                        onClick={() => refreshCatalog?.()}
                        className="px-2 py-0.5 text-xs rounded bg-red-900/50 hover:bg-red-900/80 text-white font-medium inline-flex items-center gap-1 transition-colors"
                      >
                        <RefreshCw size={12} className={loadingCatalog ? "animate-spin" : ""} />
                        {t.connections?.catalogErrorRetry || "Reintentar"}
                      </button>
                    </div>
                  </div>
                )}
                {isGuest && !catalogError && !anyKeyConfigured && (
                  <div data-testid="guest-key-required-banner" className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
                    <div className="font-semibold text-amber-400">Clave de Apimart requerida</div>
                    <p className="mt-1 text-[11px] leading-relaxed text-amber-200/90">
                      Configura tu clave de Apimart para generar.{" "}
                      <Link to="/settings#connections" className="underline font-medium text-amber-300">
                        Ajustes → Conexiones
                      </Link>
                    </p>
                  </div>
                )}
                {isGuest && anyKeyConfigured && !hasBudgetLimit && (
                  <div data-testid="guest-budget-required-banner" className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
                    <div className="font-semibold text-amber-400">Límite mensual requerido</div>
                    <p className="mt-1 text-[11px] leading-relaxed text-amber-200/90">
                      Debes establecer un límite mensual antes de generar.{" "}
                      <Link to="/settings#connections" className="underline font-medium text-amber-300">
                        Ajustes → Conexiones
                      </Link>
                    </p>
                  </div>
                )}
                {isGuest && hasBudgetLimit && isBudgetInsufficient && (
                  <div data-testid="guest-budget-insufficient-banner" className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200">
                    <div className="font-semibold text-red-400">Presupuesto insuficiente</div>
                    <p className="mt-1 text-[11px] leading-relaxed text-red-200/90">
                      El presupuesto disponible (${budgetAvailable.toFixed(2)}) no cubre el coste estimado de esta generación (${cost.toFixed(2)}).
                    </p>
                  </div>
                )}
                {isModelBlocked && (
                  <div data-testid="model-blocked-banner" className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
                    <div className="font-semibold text-amber-400">Modelo no disponible</div>
                    <p className="mt-1 text-[11px] leading-relaxed text-amber-200/90">
                      {model?.pendingIntegration
                        ? "La integración técnica de este modelo está pendiente."
                        : model?.pricePending
                        ? "Precio pendiente — generación no disponible."
                        : "Este modelo no se encuentra disponible actualmente."}
                    </p>
                  </div>
                )}
                {!isGuest && budgetConfigured === false && (
                  <div data-testid="budget-not-configured-banner" className="rounded border border-[#f5b544]/40 bg-[#f5b544]/5 px-3 py-2 text-[12px] text-[#f5b544]">
                    {t.generation?.budgetNotConfigured ?? "No hay tope mensual configurado."}{" "}
                    <Link to="/settings#budget" className="underline">{t.generation?.budgetSettingsLink ?? "Ajustes › Presupuesto"}</Link>
                  </div>
                )}
                <CostEstimate
                  total={cost}
                  reserve={null}
                  budgetAvailable={budgetAvailable}
                  extraRefCost={extraRefCost}
                  priceType={model?.priceType}
                  priceVerified={model?.priceVerified}
                  pricePending={model?.pricePending}
                />

                <button
                  data-testid="tool-submit"
                  className="v-btn-primary justify-center w-full py-2.5 mt-1 font-medium shadow-md transition-all"
                  disabled={!canSubmit || submitting || isPhase5}
                  title={submitButtonTitle}
                  onClick={submit}
                >
                  {submitting ? "Enviando…" : <><Lock size={14} />{cfg.cta || "Aplicar edición"}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_420px]">
          <div className="flex flex-col gap-6">
            {renderInlineResult()}
            {needsSource && (
              <Field label={t.tools.sourceImage}>
                {renderSourcePreview()}
              </Field>
            )}

          {isVideo && (
            <Field label={t.tools.videoMode}>
              <div className="flex flex-wrap gap-1.5">
                {(model?.modes || []).map((m) => (
                  <button
                    key={m}
                    data-testid={`tool-mode-${m}`}
                    onClick={() => {
                      setMode(m);
                      if (model?.audioIncompatibleWith?.includes(m)) {
                        setAudioEnabled(false);
                      }
                    }}
                    className={`v-btn-ghost v-btn-sm ${mode === m ? "border-[#f5b544] text-[#f5b544]" : ""}`}
                  >
                    {t.tools.videoModes[m]}
                  </button>
                ))}
              </div>
            </Field>
          )}
          {isVideo && mode !== "text_to_video" && mode !== "motion_control" && (
            <Field label={t.tools.startFrame}>
              <div className="flex items-start gap-4">
                <MediaFrame asset={startAsset} className="aspect-video w-48" />
                <div className="flex flex-col gap-1.5">
                  <button data-testid="tool-pick-start" className="v-btn-ghost v-btn-sm" onClick={() => setPick("start")}>
                    {startAsset ? (t.tools.replaceSource || "Reemplazar") : t.tools.pickSource}
                  </button>
                  <button data-testid="tool-upload-start" className="v-btn-ghost v-btn-sm" disabled={uploadBusy} onClick={() => openUpload("start")}>
                    <Upload size={12} />{uploadBusy ? t.common.loading : t.tools.uploadTest}
                  </button>
                  {startAsset && (
                    <button data-testid="tool-remove-start" className="v-btn-ghost v-btn-sm text-red-400 hover:text-red-300" onClick={() => {
                      setStartId("");
                      if (mode === "first_last_frame") setMode("text_to_video");
                    }}>
                      <Trash2 size={12} />{t.tools.removeSource || "Quitar"}
                    </button>
                  )}
                </div>
              </div>
              {startId && !startAsset && (
                <p data-testid="tool-start-error" className="mt-2 text-xs text-red-400">
                  La imagen referenciada ({startId}) no existe o no pudo resolverse en la galería.
                </p>
              )}
            </Field>
          )}
          {isVideo && (mode === "first_last_frame" || (supportsFLF && mode !== "text_to_video" && mode !== "motion_control")) && (
            <Field label={t.tools.endFrame}>
              {multishotEnabled ? (
                <div data-testid="multishot-flf-disabled-notice" className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-[#ededed]">
                  <div className="flex items-center gap-1.5 font-medium text-amber-400">
                    <AlertTriangle size={14} />
                    <span>Incompatibilidad contractual: Multishot + End Frame</span>
                  </div>
                  <p className="mt-1 text-[11px] leading-relaxed text-[#a3a3a3]">
                    Kling 3.0 no admite combinar secuencias Multishot con fotograma final de cierre (FLF). El fotograma final no se enviará mientras Multishot esté activo.
                  </p>
                  <div className="mt-2.5 flex items-center gap-2">
                    <button
                      type="button"
                      data-testid="multishot-keep-multishot-btn"
                      className="rounded border border-[#303030] bg-[#1f1f1f] px-2.5 py-1 text-[11px] font-medium text-[#ededed] hover:bg-[#2a2a2a]"
                      onClick={resolveConflictDisableEnd}
                    >
                      Continuar con Multishot (desactivar End Frame)
                    </button>
                    <button
                      type="button"
                      data-testid="multishot-keep-flf-btn"
                      className="rounded border border-[#303030] bg-transparent px-2.5 py-1 text-[11px] text-[#a3a3a3] hover:text-[#ededed]"
                      onClick={() => {
                        setManualScenesEnabled(false);
                        setMultishotConflict(false);
                      }}
                    >
                      Mantener End Frame (desactivar Multishot)
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-4">
                    <MediaFrame asset={endAsset} className="aspect-video w-48" />
                    <div className="flex flex-col gap-1.5">
                      <button data-testid="tool-pick-end" className="v-btn-ghost v-btn-sm" onClick={() => setPick("end")}>
                        {endAsset ? (t.tools.replaceSource || "Reemplazar") : t.tools.pickSource}
                      </button>
                      <button data-testid="tool-upload-end" className="v-btn-ghost v-btn-sm" disabled={uploadBusy} onClick={() => openUpload("end")}>
                        <Upload size={12} />{uploadBusy ? t.common.loading : t.tools.uploadTest}
                      </button>
                      {endAsset && (
                        <button data-testid="tool-remove-end" className="v-btn-ghost v-btn-sm text-red-400 hover:text-red-300" onClick={() => {
                          setEndId("");
                          if (mode === "first_last_frame" && startId) setMode("image_to_video");
                        }}>
                          <Trash2 size={12} />{t.tools.removeSource || "Quitar"}
                        </button>
                      )}
                    </div>
                  </div>
                  {endId && !endAsset && (
                    <p data-testid="tool-end-error" className="mt-2 text-xs text-red-400">
                      La imagen referenciada ({endId}) no existe o no pudo resolverse en la galería.
                    </p>
                  )}
                </>
              )}
            </Field>
          )}
          {isVideo && mode === "motion_control" && (
            <Field label={t.tools.motionVideo || "Vídeo de movimiento de referencia"}>
              <div className="flex items-start gap-4">
                <MediaFrame asset={motionVideoAsset} className="aspect-video w-48" controls={Boolean(motionVideoAsset)} />
                <div className="flex flex-col gap-1.5">
                  <button data-testid="tool-pick-motion" className="v-btn-ghost v-btn-sm" onClick={() => setPick("motion")}>
                    {motionVideoAsset ? (t.tools.replaceSource || "Reemplazar") : t.tools.pickSource}
                  </button>
                  <button data-testid="tool-upload-motion" className="v-btn-ghost v-btn-sm" disabled={uploadBusy} onClick={() => openUpload("motion")}>
                    <Upload size={12} />{uploadBusy ? t.common.loading : t.tools.uploadTest}
                  </button>
                  {motionVideoAsset && (
                    <button data-testid="tool-remove-motion" className="v-btn-ghost v-btn-sm text-red-400 hover:text-red-300" onClick={() => setMotionVideoId("")}>
                      <Trash2 size={12} />{t.tools.removeSource || "Quitar"}
                    </button>
                  )}
                </div>
              </div>
              {motionVideoId && !motionVideoAsset && (
                <p data-testid="tool-motion-error" className="mt-2 text-xs text-red-400">
                  El vídeo de movimiento referenciado ({motionVideoId}) no existe o no pudo resolverse en la galería.
                </p>
              )}
            </Field>
          )}
          {isVideo && frameMismatch && (
            <Notice tone="warn" testId="tool-frame-mismatch"><AlertTriangle size={13} className="mr-1 inline" />{t.tools.frameFormatMismatch}</Notice>
          )}

          {isVideo && isSceneCompatible ? (
            <div
              data-testid="cinematic-scenes-container"
              className="rounded-xl border border-[#262626] bg-[#141414] overflow-hidden transition-all"
            >
              {/* Encabezado compacto y selector de dos opciones */}
              <div
                data-testid="cinematic-scenes-header"
                className="flex flex-wrap items-center justify-between gap-2 border-b border-[#222] bg-[#171717] px-4 py-2.5 text-xs"
              >
                <div className="flex items-center gap-2">
                  <div
                    className={`flex h-6 w-6 items-center justify-center rounded-md ${
                      sceneCaps.strategy === "structured"
                        ? "bg-sky-500/10 text-sky-400"
                        : "bg-amber-500/10 text-amber-400"
                    }`}
                  >
                    <Clapperboard size={13} />
                  </div>
                  <span className="font-semibold text-[#ededed]">
                    Escenas cinematográficas
                  </span>
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium border ${
                      sceneCaps.strategy === "structured"
                        ? "border-sky-500/30 bg-sky-500/20 text-sky-400"
                        : "border-amber-500/30 bg-amber-500/20 text-amber-400"
                    }`}
                  >
                    {sceneCaps.label}
                  </span>
                  {multishotEnabled && (
                    <span
                      data-testid="cinematic-summary-badge"
                      className="rounded bg-[#222] px-2 py-0.5 text-[10px] font-medium text-[#a3a3a3]"
                    >
                      {activeScenes.length} {activeScenes.length === 1 ? "escena" : "escenas"} · {totalShotDuration}s
                    </span>
                  )}
                </div>

                {/* Selector de dos opciones: [ Editor visual ] [ Escritura libre ] */}
                <div className="inline-flex rounded-lg border border-[#2e2e2e] bg-[#0d0d0d] p-0.5" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={cinematicMode === "visual"}
                    data-testid="cinematic-mode-visual-btn"
                    onClick={() => handleSwitchCinematicMode("visual")}
                    className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium transition-all ${
                      cinematicMode === "visual"
                        ? "bg-[#262626] text-white shadow-sm"
                        : "text-[#888] hover:text-[#ccc]"
                    }`}
                  >
                    <LayoutGrid size={12} />
                    <span>Editor visual</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={cinematicMode === "freeform"}
                    data-testid="cinematic-mode-freeform-btn"
                    onClick={() => handleSwitchCinematicMode("freeform")}
                    className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium transition-all ${
                      cinematicMode === "freeform"
                        ? "bg-[#262626] text-white shadow-sm"
                        : "text-[#888] hover:text-[#ccc]"
                    }`}
                  >
                    <FileText size={12} />
                    <span>Escritura libre</span>
                  </button>
                </div>
              </div>

              {/* Contenido: Se muestra ÚNICAMENTE el editor seleccionado */}
              <div className="p-4">
                {cinematicMode === "visual" ? (
                  /* ── Modo Editor Visual ────────────────────────── */
                  <div data-testid="kling-multishot-panel" className="flex flex-col gap-3">
                    {/* Prompt tradicional detectado sin @Escena */}
                    {!hasDetectedScenes && prompt.trim() && (
                      <div className="flex items-center justify-between rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs text-amber-200">
                        <div className="flex items-center gap-2">
                          <Info size={14} className="text-amber-400 shrink-0" />
                          <span>
                            Prompt previo: <em>"{prompt.slice(0, 45)}{prompt.length > 45 ? "…" : ""}"</em>
                          </span>
                        </div>
                        <button
                          type="button"
                          data-testid="convert-prompt-to-scene-btn"
                          onClick={handleConvertPromptToScene1}
                          className="rounded bg-amber-500/20 px-2.5 py-1 text-[11px] font-medium text-amber-300 hover:bg-amber-500/30 shrink-0"
                        >
                          Usar como Escena 1
                        </button>
                      </div>
                    )}

                    {/* Preámbulo / estilo general si existe */}
                    {promptScenes.preamble && (
                      <div className="rounded-lg border border-[#2a2a2a] bg-[#171717] p-2.5 text-xs text-[#a3a3a3]">
                        <span className="font-semibold text-[#888]">Estilo general / Preámbulo:</span> {promptScenes.preamble}
                      </div>
                    )}

                    {/* Conflicto con End Frame */}
                    {multishotConflict && (
                      <div
                        data-testid="multishot-conflict-banner"
                        className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200"
                      >
                        <div className="flex items-center gap-1.5 font-medium text-amber-400">
                          <AlertTriangle size={14} />
                          <span>Conflicto de modalidad con End Frame</span>
                        </div>
                        <p className="mt-1 text-[11px] text-amber-200/80">
                          Las secuencias multiescena no son compatibles con fotograma de cierre. Para continuar con escenas, se desactivará el End Frame manteniendo la imagen inicial (I2V).
                        </p>
                        <div className="mt-2 flex gap-2">
                          <button
                            type="button"
                            data-testid="multishot-conflict-resolve-btn"
                            className="rounded bg-amber-500/20 px-2 py-1 text-[11px] font-medium text-amber-300 hover:bg-amber-500/30"
                            onClick={resolveConflictDisableEnd}
                          >
                            Desactivar End Frame y continuar
                          </button>
                          <button
                            type="button"
                            className="rounded border border-[#303030] px-2 py-1 text-[11px] text-[#a3a3a3] hover:text-white"
                            onClick={() => {
                              setManualScenesEnabled(false);
                              setMultishotConflict(false);
                            }}
                          >
                            Cancelar Escenas
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Barra de Validación de Duración */}
                    <div
                      data-testid="multishot-duration-bar"
                      className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border p-2.5 text-xs ${
                        shotDurationMismatch
                          ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                          : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">
                          Duración escenas: {totalShotDuration}s / {duration}s total
                        </span>
                        {shotDurationMismatch ? (
                          <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">
                            Desfase de {Math.abs(duration - totalShotDuration)}s
                          </span>
                        ) : (
                          <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-medium text-emerald-300">
                            Suma correcta
                          </span>
                        )}
                      </div>
                      {shotDurationMismatch && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={applyScenesDurationToModel}
                            className="rounded border border-amber-500/40 bg-amber-500/20 px-2 py-0.5 text-[11px] font-medium text-amber-200 hover:bg-amber-500/30"
                          >
                            Ajustar modelo a {totalShotDuration}s
                          </button>
                          <button
                            type="button"
                            data-testid="multishot-auto-adjust-btn"
                            className="rounded border border-amber-500/40 bg-amber-500/20 px-2 py-0.5 text-[11px] font-medium text-amber-200 hover:bg-amber-500/30"
                            onClick={autoAdjustLastShot}
                          >
                            Ajustar última escena automáticamente
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Lista de Escenas */}
                    <div className="flex flex-col gap-2.5">
                      {activeScenes.map((scene, idx) => {
                        const pLen = (scene.prompt || "").length;
                        const isOverLimit = pLen > maxCharsPerScene;
                        return (
                          <div
                            key={idx}
                            data-testid={`multishot-shot-item-${idx}`}
                            className="flex flex-col gap-2 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a] p-3"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-[#ededed]">
                                Escena #{idx + 1}
                              </span>
                              <div className="flex items-center gap-3">
                                <label className="flex items-center gap-1.5 text-[11px] text-[#a3a3a3]">
                                  <span>Duración:</span>
                                  <input
                                    type="number"
                                    data-testid={`multishot-duration-input-${idx}`}
                                    min={1}
                                    max={sceneCaps.maxSceneDuration || 15}
                                    value={scene.duration}
                                    onChange={(e) => updateShotDuration(idx, e.target.value)}
                                    className="w-14 rounded border border-[#333] bg-[#111] px-2 py-0.5 text-center text-xs text-white focus:border-amber-500 focus:outline-none"
                                  />
                                  <span>s</span>
                                </label>
                                {activeScenes.length > 1 && (
                                  <button
                                    type="button"
                                    data-testid={`multishot-delete-shot-${idx}`}
                                    className="text-[#737373] hover:text-red-400"
                                    title="Eliminar escena"
                                    onClick={() => deleteShot(idx)}
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                )}
                              </div>
                            </div>

                            <div className="flex flex-col gap-1">
                              <textarea
                                data-testid={`multishot-prompt-input-${idx}`}
                                rows={2}
                                maxLength={maxCharsPerScene + 100}
                                value={scene.prompt}
                                onChange={(e) => updateShotPrompt(idx, e.target.value)}
                                placeholder={`Describe la acción, encuadre y cámara para la Escena ${idx + 1}...`}
                                className={`w-full resize-none rounded border bg-[#111] p-2 text-xs text-white placeholder-[#555] focus:outline-none ${
                                  isOverLimit ? "border-red-500 focus:border-red-500" : "border-[#333] focus:border-amber-500"
                                }`}
                              />
                              <div className="flex items-center justify-between text-[10px]">
                                <span className={isOverLimit ? "text-red-400" : "text-[#666]"}>
                                  {pLen}/{maxCharsPerScene} caracteres {isOverLimit && "(supera el límite del modelo)"}
                                </span>
                                {!scene.prompt?.trim() && (
                                  <span className="text-amber-400/80">Descripción de escena requerida</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Botón Agregar Escena */}
                    <div className="flex items-center justify-between pt-1">
                      <button
                        type="button"
                        data-testid="multishot-add-shot-btn"
                        disabled={activeScenes.length >= (sceneCaps.maxScenes || 6)}
                        onClick={addShot}
                        className="rounded border border-[#303030] bg-[#1a1a1a] px-3 py-1 text-xs font-medium text-[#ededed] hover:bg-[#252525] disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        + Agregar escena ({activeScenes.length}/{sceneCaps.maxScenes || 6})
                      </button>
                      <span className="text-[11px] text-[#666]">
                        {activeScenes.length >= (sceneCaps.maxScenes || 6)
                          ? `Límite máximo de ${sceneCaps.maxScenes || 6} escenas alcanzado`
                          : `Hasta ${sceneCaps.maxScenes || 6} escenas consecutivas`}
                      </span>
                    </div>
                  </div>
                ) : (
                  /* ── Modo Escritura Libre ────────────────────────── */
                  <div className="flex flex-col gap-2">
                    <PromptMentionEditor
                      value={prompt}
                      onChange={setPrompt}
                      placeholder="@Escena 1 [3s]\nDescripción...\n\n@Escena 2 [2s]\nDescripción..."
                      rows={6}
                      testId="tool-prompt"
                      startAsset={startAsset}
                      endAsset={endAsset}
                      supportsFLF={supportsFLF && !multishotEnabled}
                      supportsScenes={true}
                      characters={state.characters || []}
                      characterReferences={characterRefs}
                      assetById={assetById}
                      onSelectCharacterMention={handleOpenCharacterViews}
                      defaultSceneDuration={Math.min(3, duration)}
                      onMentionInsert={(tag) => {
                        if (tag === "@endframe" && supportsFLF && !multishotEnabled) {
                          setMode("first_last_frame");
                        }
                      }}
                      disabled={submitting}
                      maxChars={maxPromptChars}
                      modelLabel={model?.label || model?.id}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            tool !== "upscale" && (
              <Field label={t.common.prompt}>
                {isVideo ? (
                  <PromptMentionEditor
                    value={prompt}
                    onChange={setPrompt}
                    placeholder={t.tools.promptPh}
                    rows={6}
                    testId="tool-prompt"
                    startAsset={startAsset}
                    endAsset={endAsset}
                    supportsFLF={supportsFLF}
                    supportsScenes={false}
                    characters={state.characters || []}
                    characterReferences={characterRefs}
                    assetById={assetById}
                    onSelectCharacterMention={handleOpenCharacterViews}
                    onMentionInsert={(tag) => {
                      if (tag === "@endframe" && supportsFLF) {
                        setMode("first_last_frame");
                      }
                    }}
                    disabled={submitting}
                    maxChars={maxPromptChars}
                    modelLabel={model?.label || model?.id}
                  />
                ) : (
                  <PromptMentionEditor
                    value={prompt}
                    onChange={setPrompt}
                    placeholder={tool === "edit" ? t.editor.instructionsPh : t.tools.promptPh}
                    rows={6}
                    testId="tool-prompt"
                    characters={state.characters || []}
                    characterReferences={characterRefs}
                    assetById={assetById}
                    onSelectCharacterMention={handleOpenCharacterViews}
                    disabled={submitting}
                    maxChars={maxPromptChars}
                    modelLabel={model?.label || model?.id}
                  />
                )}
              </Field>
            )
          )}
          {(isImage || (isVideo && maxRefs > 0)) && (
            <Field label={`${t.tools.references} (máx. ${maxRefs})`} hint={t.tools.uploadTestNote}>
              {maxRefs <= 2 && (
                <p className="mb-1.5 text-[10px] text-[#5a5a5a]" data-testid="refs-cap-note">
                  {t.tools.refsCap ?? `Máx. ${maxRefs} referencias por generación.`}
                </p>
              )}
              <ReferenceChips
                references={state.references}
                selectedIds={refs}
                assetById={assetById}
                onToggle={(id) => {
                  const refAssetId = state.references?.find((r) => r.id === id)?.assetId;
                  if (!refs.includes(id)) {
                    if (refsAtMax) {
                      toast.warning(`Máximo ${maxRefs} referencias en esta fase`);
                      return;
                    }
                    if (refAssetId && allActiveRefAssetIds.includes(refAssetId)) {
                      toast.info("Esta referencia ya está seleccionada");
                      return;
                    }
                  }
                  setRefs((x) => (x.includes(id) ? x.filter((i) => i !== id) : [...x, id]));
                }}
                extraAssets={refAssetIds.map((id) => assetById[id]).filter(Boolean)}
                onRemoveExtra={(id) => setRefAssetIds((x) => x.filter((i) => i !== id))}
                characterReferences={characterRefs}
                onRemoveCharacterRef={handleRemoveCharacterRef}
                onChangeCharacterRef={handleChangeCharacterRef}
              />
              {refsAtMax && <p className="mt-1 text-xs text-[#f5b544]">{`Límite de ${maxRefs} referencias alcanzado`}</p>}
              {refsIncompatible && (
                <div className="mt-2" data-testid="refs-incompatible-notice">
                  <Notice tone="warn">
                    {isVideo
                      ? (t.tools?.videoRefsIncompatible || "La modalidad de vídeo no admite imágenes de referencia generales.")
                      : (t.tools?.modelRefsIncompatible ? t.tools.modelRefsIncompatible(model?.label || modelId) : "El modelo seleccionado no admite imágenes de referencia.")}
                  </Notice>
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button data-testid="tool-pick-ref" className="v-btn-ghost v-btn-sm" disabled={refsAtMax} onClick={() => setPick("ref")}>{t.tools.pickSource}</button>
                <button data-testid="tool-upload-ref" className="v-btn-ghost v-btn-sm" disabled={uploadBusy || refsAtMax} onClick={() => openUpload("ref")}><Upload size={12} />{uploadBusy ? t.common.loading : t.tools.uploadTest}</button>
              </div>
            </Field>
          )}
          {tool === "edit" && (
            <Field label={`${t.tools.references} (máx. ${maxRefs})`} hint={t.tools.uploadTestNote}>
              <ReferenceChips references={state.references} selectedIds={refs} assetById={assetById} onToggle={(id) => { if (!refs.includes(id) && refsAtMax) { toast.warning(`Máximo ${maxRefs} referencias en esta fase`); return; } setRefs((x) => (x.includes(id) ? x.filter((i) => i !== id) : [...x, id])); }} extraAssets={refAssetIds.map((id) => assetById[id]).filter(Boolean)} onRemoveExtra={(id) => setRefAssetIds((x) => x.filter((i) => i !== id))} />
              {refsAtMax && <p className="mt-1 text-xs text-[#f5b544]">{`Límite de ${maxRefs} referencias alcanzado`}</p>}
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button data-testid="tool-pick-ref-edit" className="v-btn-ghost v-btn-sm" disabled={refsAtMax} onClick={() => setPick("ref")}>{t.tools.pickSource}</button>
                <button data-testid="tool-upload-ref-edit" className="v-btn-ghost v-btn-sm" disabled={uploadBusy || refsAtMax} onClick={() => openUpload("ref")}><Upload size={12} />{uploadBusy ? t.common.loading : t.tools.uploadTest}</button>
              </div>
              <p className="mt-1.5 text-[11px] text-[#6b6b6b]">El original se preserva; la edición crea un activo nuevo.</p>
            </Field>
          )}
        </div>

        <aside className="flex flex-col gap-4">
          <div className="v-panel flex flex-col gap-4 p-5">
            {isPhase5 ? (
              <div className="flex flex-col gap-2">
                <Notice tone="warn" testId="tool-phase5-notice">{t.tools.phase5Notice}</Notice>
                <p className="text-xs text-[#6b6b6b]">{t.tools.phase5Body}</p>
              </div>
            ) : isUpscale ? (
              <>
                {/* ── Panel de Upscale (fal.ai) ──────────────────────── */}
                {isGuest ? (
                  <Notice tone="warn" testId="tool-upscale-guest-notice">
                    La herramienta de reescalado (upscale) depende del proveedor fal.ai y no está disponible para cuentas invitadas. Solo Apimart está habilitado.
                  </Notice>
                ) : !upscaleFalConfigured && (
                  <Notice tone="warn" testId="tool-upscale-no-key">
                    FAL_KEY no configurada. El upscaler requiere la clave fal.ai en el servidor.
                  </Notice>
                )}
                <Field label={t.tools.upscaleModel}>
                  <select
                    data-testid="upscale-model-select"
                    className="v-input"
                    value={upscaleModelId}
                    onChange={(e) => {
                      setUpscaleModelId(e.target.value);
                      const m = upscaleModels.find((x) => x.id === e.target.value);
                      const scales = m?.upscaleScales || [2, 4];
                      setUpscaleScale(scales[0] || 2);
                    }}
                  >
                    {upscaleModels.length === 0
                      ? <option value="">Cargando modelos…</option>
                      : upscaleModels.map((m) => (
                        <option key={m.id} value={m.id}>{m.label}</option>
                      ))
                    }
                  </select>
                </Field>
                <Field label={t.tools.upscaleScale}>
                  <select
                    data-testid="upscale-scale-select"
                    className="v-input"
                    value={upscaleScale}
                    onChange={(e) => setUpscaleScale(Number(e.target.value))}
                  >
                    {upscaleScales.map((s) => (
                      <option key={s} value={s}>{t.tools.upscaleScaleOption(s)}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Formato de salida">
                  <select
                    data-testid="upscale-format-select"
                    className="v-input"
                    value={upscaleFormat}
                    onChange={(e) => setUpscaleFormat(e.target.value)}
                  >
                    {(upscaleModel?.output_formats || ["jpeg", "png"]).map((f) => (
                      <option key={f} value={f}>{f.toUpperCase()}</option>
                    ))}
                  </select>
                </Field>
                {/* Reserva de presupuesto */}
                <div data-testid="upscale-reserve-notice"
                  className="rounded border border-[#f5b544]/40 bg-[#f5b544]/5 px-3 py-2 text-[11px] text-[#f5b544]">
                  {t.tools.upscaleReserveNotice(upscaleReserve.toFixed(2))}
                </div>
                <p className="text-[10px] text-[#6b6b6b]">{t.tools.upscalePreservedNote}</p>
                {/* Presupuesto global */}
                {budgetConfigured === false && (
                  <div data-testid="budget-not-configured-banner"
                    className="rounded border border-[#f5b544]/40 bg-[#f5b544]/5 px-3 py-2 text-[12px] text-[#f5b544]">
                    {t.generation?.budgetNotConfigured ?? "No hay tope mensual configurado."}{" "}
                    <Link to="/settings#budget" className="underline">{t.generation?.budgetSettingsLink ?? "Ajustes › Presupuesto"}</Link>
                  </div>
                )}
              </>
            ) : (isImage || isVideo || isEdit) ? (
              <>
                <ConnectionModelPicker
                  connections={eligibleConnections}
                  connectionId={connectionId}
                  onConnection={handleConnectionChange}
                  models={models}
                  modelId={modelId}
                  onModel={setModelId}
                  testPrefix="tool"
                  loading={loadingCatalog}
                  error={Boolean(catalogError)}
                />
                {model?.pricePending && (
                  <div data-testid="tool-price-pending-notice" className="mt-1.5 rounded border border-[#f5b544]/40 bg-[#f5b544]/10 p-2.5 text-xs text-[#f5b544]">
                    <span className="font-semibold">Precio pendiente — generación no disponible:</span> Este modelo está técnicamente integrado, pero su tarifa aún no ha sido verificada.
                  </div>
                )}
                <p className="text-[11px] text-[#a3a3a3]">{t.tools.modelNote}</p>
                {(isImage || isEdit) && (
                  <Field label={t.tools.format}>
                    <FormatPicker formats={model?.formats || []} extendedFormats={model?.extendedFormats || []} value={format} onChange={setFormat} testPrefix="tool-format" />
                  </Field>
                )}
                {isVideo && (
                  <Field label={t.tools.format}>
                    <FormatPicker formats={model?.formats || []} extendedFormats={[]} value={videoFormat} onChange={setVideoFormat} testPrefix="tool-format" />
                  </Field>
                )}
                {isImage && (
                  <Field label={t.tools.quantity}>
                    {(() => {
                      const qOpts = QUANTITIES.filter((q) => q <= (model?.maxQuantity || 4));
                      return (
                        <select
                          data-testid="tool-quantity-select"
                          className="v-input"
                          value={quantity}
                          disabled={qOpts.length <= 1}
                          onChange={(e) => setQuantity(Number(e.target.value))}
                          aria-label={t.tools.quantity}
                        >
                          {qOpts.map((q) => <option key={q} value={q}>{q}</option>)}
                        </select>
                      );
                    })()}
                  </Field>
                )}
                <Field label={t.tools.resolution}>
                  {(() => {
                    const allRes = (isImage || isEdit ? IMAGE_RESOLUTIONS : VIDEO_RESOLUTIONS);
                    const supported = model?.resolutions?.length > 0
                      ? model.resolutions
                      : allRes;
                    const currentVal = isVideo ? videoResolution : resolution;
                    const handleChange = (newVal) => {
                      if (isVideo) setVideoResolution(newVal);
                      else setResolution(newVal);
                    };

                    const showPills = isKling || (supported.length > 1 && supported.length <= 4);

                    return (
                      <div className="flex flex-col gap-2">
                        {showPills && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="tool-resolution-pills">
                            {supported.map((r) => {
                              const label = formatResolutionLabel(r, model);
                              const isSelected = currentVal === r || (isKling && (
                                ((r === "std" || r === "720P" || r === "720p") && (currentVal === "std" || currentVal === "720P" || currentVal === "720p")) ||
                                ((r === "pro" || r === "1080P" || r === "1080p") && (currentVal === "pro" || currentVal === "1080P" || currentVal === "1080p"))
                              ));
                              return (
                                <button
                                  key={r}
                                  type="button"
                                  data-testid={`resolution-pill-${r}`}
                                  onClick={() => handleChange(r)}
                                  className={`flex items-center justify-between px-3 py-2.5 rounded-lg border text-xs transition-all ${
                                    isSelected
                                      ? "border-[#f5b544] bg-[#f5b544]/10 text-[#f5b544] font-semibold ring-1 ring-[#f5b544]/40"
                                      : "border-[#2a2a2a] bg-[#141414] text-[#a3a3a3] hover:border-[#404040] hover:text-[#ededed]"
                                  }`}
                                >
                                  <span className="truncate">{label}</span>
                                  {isSelected ? (
                                    <span className="h-2 w-2 rounded-full bg-[#f5b544] shrink-0" />
                                  ) : (
                                    <span className="h-2 w-2 rounded-full border border-[#444] shrink-0" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        <select
                          data-testid="tool-resolution-select"
                          className={`v-input ${showPills ? "hidden" : ""}`}
                          value={currentVal}
                          disabled={supported.length <= 1}
                          onChange={(e) => handleChange(e.target.value)}
                          aria-label={t.tools.resolution}
                        >
                          {supported.map((r) => (
                            <option key={r} value={r}>
                              {formatResolutionLabel(r, model)}
                            </option>
                          ))}
                        </select>

                        {/* Nota contextual para Kling */}
                        {isKling && (
                          <div className="mt-1">
                            {isKlingProMode ? (
                              <div
                                data-testid="kling-pro-note"
                                className="flex items-start gap-1.5 rounded-md border border-amber-500/25 bg-amber-500/5 p-2 text-[11px] text-amber-300/90"
                              >
                                <span>ℹ️</span>
                                <span>
                                  <strong>1080p — Professional (PRO):</strong> APIMart no publica tarifa oficial confirmada para PRO. El cálculo estimado se mantiene como no disponible para evitar estimaciones engañosas; el coste real se confirmará tras la generación.
                                </span>
                              </div>
                            ) : (
                              <div
                                data-testid="kling-std-note"
                                className="flex items-start gap-1.5 rounded-md border border-emerald-500/20 bg-emerald-500/5 p-2 text-[11px] text-emerald-400/90"
                              >
                                <span>✓</span>
                                <span>
                                  <strong>720p — Standard (STD):</strong> Tarifa oficial de APIMart confirmada a $0.0672/s (sin audio).
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </Field>
                {isVideo && (
                  <Field label={t.tools.duration} hint={t.tools.durationNote}>
                    <select
                      data-testid="tool-duration-select"
                      className="v-input"
                      value={duration}
                      onChange={(e) => setDuration(Number(e.target.value))}
                      aria-label={t.tools.duration}
                    >
                      {(model?.durations?.length > 0 ? model.durations : [2, 3, 4, 5, 6, 7, 8, 9, 10]).map((d) => (
                        <option key={d} value={d}>{`${d}s`}</option>
                      ))}
                    </select>
                  </Field>
                )}
                {isVideo && model?.audioSupported && (
                  <Field label="Audio nativo">
                    <div className="flex flex-col gap-1.5">
                      <label className={`flex items-center gap-2.5 rounded-lg border p-2.5 cursor-pointer select-none transition-colors ${
                        audioEnabled ? "border-[#f5b544]/50 bg-[#f5b544]/5" : "border-[#262626] bg-[#141414] hover:border-[#333]"
                      }`}>
                        <input
                          type="checkbox"
                          data-testid="tool-audio-toggle"
                          checked={audioEnabled}
                          disabled={model?.audioIncompatibleWith?.includes(mode)}
                          onChange={(e) => setAudioEnabled(e.target.checked)}
                          className="h-4 w-4 rounded border-[#303030] text-[#f5b544] focus:ring-0"
                        />
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-[#ededed]">
                              {audioEnabled ? "🔊 Generar con audio" : "🔇 Vídeo silencioso"}
                            </span>
                            {model.audioSurchargeRateUsd > 0 ? (
                              <span className="rounded bg-[#262626] px-1.5 py-0.5 text-[10px] text-[#f5b544]">
                                +${(duration * model.audioSurchargeRateUsd).toFixed(3)} USD
                              </span>
                            ) : (
                              <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-400">
                                Incluido ($0 extra)
                              </span>
                            )}
                          </div>
                          <span className="text-[10px] text-[#737373]">
                            {model.id?.startsWith("kling-")
                              ? "Audio ambiente y SFX en modo Pro (1080P). Requiere máx. 1 fotograma de referencia."
                              : model.id === "veo-3-1" || model.id?.startsWith("veo")
                                ? "Diálogos entre comillas, efectos sonoros y ambiente sincrónico."
                                : model.id === "pixverse-v6"
                                  ? "Efectos sonoros, música de fondo y ambiente adaptados."
                                  : model.id?.startsWith("seedance")
                                    ? "Audio ambiental y SFX nativo de Seedance (generate_audio, incluido)."
                                    : model.id?.startsWith("wan")
                                      ? "Audio sincrónico nativo de Wan 3.0 (incluido en tarifa base)."
                                      : "Efectos sonoros y sonido ambiental sincronizado."}
                          </span>
                        </div>
                      </label>
                      {model?.audioIncompatibleWith?.includes(mode) && (
                        <p data-testid="tool-audio-flf-warning" className="text-[11px] text-amber-400/90 leading-tight">
                          ⚠️ El audio nativo no está disponible con {mode === "first_last_frame" ? "fotograma final (FLF)" : mode} en {model?.label || model?.id} (restricción contractual del proveedor: máx. 1 imagen).
                        </p>
                      )}
                    </div>
                  </Field>
                )}
                {incompatible && <Notice tone="warn" testId="tool-incompatible-notice">{t.tools.incompatibleSelection}</Notice>}
                {isVideo && <p className="text-[10px] text-[#6b6b6b]">{t.tools.nativeNote}</p>}
                {isEdit && (
                  <p className="text-[10px] text-[#6b6b6b]">
                    {t.tools.editOriginalPreserved ?? "El original se conserva. La edición genera un activo nuevo con parentAssetId apuntando al original."}
                  </p>
                )}
                {/* Bloque de presupuesto — visible antes de generar */}
                {budgetConfigured === false && (
                  <div data-testid="budget-not-configured-banner" className="rounded border border-[#f5b544]/40 bg-[#f5b544]/5 px-3 py-2 text-[12px] text-[#f5b544]">
                    {t.generation?.budgetNotConfigured ?? "No hay tope mensual configurado."}{" "}
                    <Link to="/settings#budget" className="underline">{t.generation?.budgetSettingsLink ?? "Ajustes › Presupuesto"}</Link>
                  </div>
                )}
                {budgetConfigured === true && (
                  <CostEstimate
                    total={cost}
                    reserve={isVideo ? preventiveReserve : null}
                    budgetAvailable={budgetAvailable}
                    extraRefCost={extraRefCost}
                    priceType={isKlingProMode ? "not_available" : model?.priceType}
                    priceVerified={isKlingProMode ? false : model?.priceVerified}
                    pricePending={isKlingProMode ? true : model?.pricePending}
                  />
                )}
                {budgetConfigured === null && (
                  <CostEstimate
                    total={cost}
                    reserve={isVideo ? preventiveReserve : null}
                    budgetAvailable={budgetAvailable}
                    extraRefCost={extraRefCost}
                    priceType={isKlingProMode ? "not_available" : model?.priceType}
                    priceVerified={isKlingProMode ? false : model?.priceVerified}
                    pricePending={isKlingProMode ? true : model?.pricePending}
                  />
                )}
                {/* Bloque de autorización de un solo uso para Kling 3 */}
                {isKling && (
                  <div
                    data-testid="kling-auth-status-card"
                    className="mt-2 flex flex-col gap-1.5 rounded-lg border p-3 text-xs"
                    style={{
                      borderColor: klingAuthAvailable ? "rgba(16, 185, 129, 0.4)" : klingAuth?.consumed ? "rgba(245, 181, 68, 0.4)" : "rgba(56, 189, 248, 0.4)",
                      backgroundColor: klingAuthAvailable ? "rgba(16, 185, 129, 0.08)" : klingAuth?.consumed ? "rgba(245, 181, 68, 0.08)" : "rgba(56, 189, 248, 0.08)",
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 font-medium" style={{
                        color: klingAuthAvailable ? "#34d399" : klingAuth?.consumed ? "#f5b544" : "#38bdf8",
                      }}>
                        <span className={`h-2 w-2 rounded-full ${klingAuthAvailable ? "bg-emerald-400 animate-pulse" : klingAuth?.consumed ? "bg-amber-400" : "bg-sky-400"}`} />
                        <span data-testid="kling-auth-state-label">
                          {klingAuthAvailable
                            ? "Autorización de un solo uso ACTIVA"
                            : klingAuth?.consumed
                              ? "Autorización de un solo uso CONSUMIDA"
                              : "Autorización de un solo uso PENDIENTE"}
                        </span>
                      </div>
                      <button
                        type="button"
                        data-testid="kling-reauthorize-btn"
                        className="rounded border border-[#303030] bg-[#1a1a1a] px-2 py-0.5 text-[11px] font-medium text-[#ededed] hover:bg-[#262626]"
                        onClick={handleAuthorizeKling}
                        disabled={authorizingKling}
                      >
                        {authorizingKling ? "Actualizando…" : klingAuthAvailable ? "Renovar permiso" : "Autorizar 1 uso"}
                      </button>
                    </div>
                    <p className="text-[11px] text-[#a3a3a3]">
                      {klingAuthAvailable
                        ? "Listo para 1 única generación real manual de Kling 3 (5s · std · I2V · sin audio). El consumo será atómico al pulsar 'Generar vídeo'."
                        : klingAuth?.consumed
                          ? "La autorización previa ya fue consumida por una generación. Para realizar otra prueba manual, pulsa 'Autorizar 1 uso'."
                          : "Kling 3 requiere autorización expresa del propietario para llamadas reales al proveedor."}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <Field label={t.tools.model} hint={t.tools.modelNote}>
                  <div data-testid="tool-model" className="flex h-10 items-center gap-2 rounded-lg border border-dashed border-[#303030] px-3 text-sm text-[#6b6b6b]"><Lock size={14} />{t.tasks.modelUnknown}</div>
                </Field>
                <CostEstimate
                  total={cost}
                  budgetAvailable={budgetAvailable}
                  priceType={model?.priceType}
                  priceVerified={model?.priceVerified}
                  pricePending={model?.pricePending}
                />
              </>
            )}
            <Field label={t.tools.project} hint={t.common.noProjectHint}>
              <select data-testid="tool-project" className="v-input" value={projectId} onChange={(e) => handleProjectChange(e.target.value)}>
                <option value="">{t.common.noProject}</option>
                {state.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
          </div>
          {isGuest && isUpscale && (
            <div data-testid="guest-upscale-disabled-banner" className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
              <div className="font-semibold text-amber-400">Reescalado no disponible</div>
              <p className="mt-1 text-[11px] leading-relaxed text-amber-200/90">
                La herramienta de reescalado depende de fal.ai y no está habilitada para cuentas invitadas. Tu cuenta opera exclusivamente con Apimart.
              </p>
            </div>
          )}
          {catalogError && (
            <div data-testid="catalog-error-drawer-banner" className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-red-400">{t.connections?.catalogLoadError || "Error al consultar conexiones."}</span>
                <button
                  type="button"
                  onClick={() => refreshCatalog?.()}
                  className="px-2 py-0.5 text-xs rounded bg-red-900/50 hover:bg-red-900/80 text-white font-medium inline-flex items-center gap-1 transition-colors"
                >
                  <RefreshCw size={12} className={loadingCatalog ? "animate-spin" : ""} />
                  {t.connections?.catalogErrorRetry || "Reintentar"}
                </button>
              </div>
            </div>
          )}
          {isGuest && !isUpscale && !catalogError && !anyKeyConfigured && (
            <div data-testid="guest-key-required-banner" className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
              <div className="font-semibold text-amber-400">Clave de Apimart requerida</div>
              <p className="mt-1 text-[11px] leading-relaxed text-amber-200/90">
                Configura tu clave de Apimart para generar.{" "}
                <Link to="/settings#connections" className="underline font-medium text-amber-300">
                  Ajustes → Conexiones
                </Link>
              </p>
            </div>
          )}
          {isGuest && !isUpscale && anyKeyConfigured && !hasBudgetLimit && (
            <div data-testid="guest-budget-required-banner" className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
              <div className="font-semibold text-amber-400">Límite mensual requerido</div>
              <p className="mt-1 text-[11px] leading-relaxed text-amber-200/90">
                Debes establecer un límite mensual antes de generar.{" "}
                <Link to="/settings#connections" className="underline font-medium text-amber-300">
                  Ajustes → Conexiones
                </Link>
              </p>
            </div>
          )}
          {isGuest && !isUpscale && hasBudgetLimit && isBudgetInsufficient && (
            <div data-testid="guest-budget-insufficient-banner" className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200">
              <div className="font-semibold text-red-400">Presupuesto insuficiente</div>
              <p className="mt-1 text-[11px] leading-relaxed text-red-200/90">
                El presupuesto disponible (${budgetAvailable.toFixed(2)}) no cubre el coste estimado de esta generación (${cost.toFixed(2)}).
              </p>
            </div>
          )}
          {isModelBlocked && (
            <div data-testid="model-blocked-banner" className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
              <div className="font-semibold text-amber-400">Modelo no disponible</div>
              <p className="mt-1 text-[11px] leading-relaxed text-amber-200/90">
                {model?.pendingIntegration
                  ? "La integración técnica de este modelo está pendiente."
                  : model?.pricePending
                  ? "Precio pendiente — generación no disponible."
                  : "Este modelo no se encuentra disponible actualmente."}
              </p>
            </div>
          )}
          <button data-testid="tool-submit" className="v-btn-primary justify-center"
            disabled={!canSubmit || submitting || isPhase5 || (isGuest && isUpscale)}
            title={submitButtonTitle}
            onClick={submit}>
            {submitting
              ? (isUpscale ? t.tools.upscaleSubmitting : "Enviando…")
              : isUpscale
                ? <><Lock size={14} />{t.tools.upscaleSubmit}</>
                : <><Lock size={14} />{cfg.cta}</>
            }
          </button>
          {!anyKeyConfigured && allowSimulate && pendingJobId && (
            <button data-testid="tool-simulate" className="v-btn-ghost v-btn-sm justify-center text-[#f5b544]" onClick={async () => { try { await simulateJob(pendingJobId); toast.success(t.tools.simulateSuccess ?? "Simulación completada — activo disponible en Galería"); } catch (e) { toast.error(e.message); } }}>{t.tools.simulateLabel ?? "Simular finalización (sin clave)"}</button>
          )}
        </aside>
      </div>
    )}
    <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => onUploadFile(e, uploadTarget)} />
      <AssetPicker open={pick === "source"} onOpenChange={(o) => !o && setPick(null)} onPick={(a) => { setSourceId(a.id); if (a.projectId) setProjectId(a.projectId); setPick(null); }} />
      <AssetPicker open={pick === "start"} onOpenChange={(o) => !o && setPick(null)} onPick={(a) => {
        setStartId(a.id);
        if (model?.modes?.includes("image_to_video") && mode === "text_to_video") {
          setMode(endId && model?.modes?.includes("first_last_frame") ? "first_last_frame" : "image_to_video");
        }
        setPick(null);
      }} />
      <AssetPicker open={pick === "end"} onOpenChange={(o) => !o && setPick(null)} onPick={(a) => {
        setEndId(a.id);
        if (model?.modes?.includes("first_last_frame")) setMode("first_last_frame");
        setPick(null);
      }} />
      <AssetPicker open={pick === "motion"} onOpenChange={(o) => !o && setPick(null)} onPick={(a) => { setMotionVideoId(a.id); setPick(null); }} />
      <AssetPicker
        open={pick === "ref"}
        onOpenChange={(o) => !o && setPick(null)}
        multi
        maxSelect={Math.max(0, maxRefs - totalRefs)}
        alreadySelected={refAssetIds}
        onPick={(picked) => {
          const arr = Array.isArray(picked) ? picked : [picked];
          setRefAssetIds((x) => {
            const existing = new Set(x);
            return [...x, ...arr.map((a) => a.id).filter((id) => !existing.has(id))].slice(0, maxRefs);
          });
          setPick(null);
        }}
      />
      <PendingDialog open={pending} onOpenChange={setPending} registered />
      <VideoPreflightModal
        open={preflightOpen}
        onClose={() => setPreflightOpen(false)}
        onConfirm={confirmVideoSubmit}
        model={model}
        videoMode={mode}
        startAsset={startAsset}
        endAsset={endAsset}
        motionVideoAsset={motionVideoAsset}
        duration={duration}
        resolution={videoResolution}
        aspectRatio={videoFormat}
        audio={audioEnabled && Boolean(model?.audioSupported)}
        cost={cost}
        reserve={preventiveReserve}
        prompt={prompt}
        isSubmitting={submitting}
        maxChars={maxPromptChars}
        multishot={multishotEnabled}
        shots={shots}
      />
      {viewerAsset && (
        <AssetDetailModal
          asset={viewerAsset}
          open={Boolean(viewerAsset)}
          onClose={() => setViewerAsset(null)}
          projectId={projectId || viewerAsset.projectId}
          onUseAsReference={(a) => {
            setRefAssetIds((x) => (x.includes(a.id) ? x : [...x, a.id]));
            toast.success("Imagen añadida como referencia en el formulario");
          }}
          onOpenInEditor={(a) => {
            const path = `/projects/${projectId || a.projectId}/editor?asset=${a.id}`;
            if (navigate) navigate(path);
            else window.location.href = path;
          }}
          testId="tool-result-detail-modal"
        />
      )}
      <CharacterViewSelectorModal
        open={Boolean(activeCharacterModal)}
        onClose={() => setActiveCharacterModal(null)}
        character={activeCharacterModal}
        assetById={assetById}
        alreadySelectedAssetIds={characterRefs.filter((cr) => cr.characterId === activeCharacterModal?.id).map((cr) => cr.assetId)}
        onConfirm={handleConfirmCharacterViews}
        maxAllowed={maxRefs}
      />
      <ReferenceLimitModal
        open={Boolean(limitResolution)}
        onClose={() => setLimitResolution(null)}
        maxAllowed={maxRefs}
        currentReferences={limitResolution?.currentReferences || []}
        candidateReferences={limitResolution?.candidateReferences || []}
        assetById={assetById}
        onConfirm={handleResolveLimit}
      />
    </Page>
  );
};

export default QuickToolPage;

// Normaliza un modelo del catálogo backend al formato que usa QuickToolPage.
// Los modelos del catálogo tienen campos snake_case (model_id, aspect_ratios, etc.);
// el frontend legacy usa camelCase (id, formats, etc.).
function normalizeCatalogModel(m) {
  return {
    id: m.model_id || m.id,
    label: m.label || m.name || m.model_id || m.id,
    provider_id: m.provider_id || m.provider || (m.providerLabel === "Kie.ai" ? "kie" : m.providerLabel === "MuAPI" ? "muapi" : "apimart"),
    providerId: m.provider_id || m.provider || (m.providerLabel === "Kie.ai" ? "kie" : m.providerLabel === "MuAPI" ? "muapi" : "apimart"),
    provider: m.provider_id || m.provider || (m.providerLabel === "Kie.ai" ? "kie" : m.providerLabel === "MuAPI" ? "muapi" : "apimart"),
    providerLabel: m.providerLabel || (m.provider_id === "kie" ? "Kie.ai" : m.provider_id === "muapi" ? "MuAPI" : "Apimart"),
    // aspect_ratios se mapea a 'formats' (el FormatPicker los llama así)
    formats: m.aspect_ratios || m.formats || [],
    extendedFormats: [],
    resolutions: m.resolutions || [],
    output_formats: m.output_formats || ["jpeg", "png"],
    // modes: leído del catálogo; permite preparar T2V cuando el backend lo soporte.
    // Para wan2.7 el backend solo soporta image_to_video; el catálogo lo declara así.
    modes: m.modes || ["text_to_video"],
    maxQuantity: m.max_quantity || 1,
    // durations: lista completa de segundos válidos para el modelo de vídeo.
    durations: m.durations || [],
    defaultDuration: m.default_duration || (m.durations?.[0] ?? 5),
    minDuration: m.min_duration || 2,
    maxDuration: m.max_duration || 15,
    costPerImage: m.estimated_cost_usd || (m.price_usd != null ? { default: m.price_usd, quality: m.price_usd } : {}),
    priceUsd: m.price_usd ?? null,
    costPerSecond: {},
    // reserveUsd: reserva máxima para precio desconocido (coincide con _VIDEO_UNKNOWN_PRICE_RESERVE_USD).
    // Usada como coste visual para vídeo cuando el proveedor no publica precio por unidad.
    reserveUsd: m.reserve_usd ?? null,
    ratePerSecondUsd: m.rate_per_second_usd ?? null,
    safetyMarginPct: m.safety_margin_pct ?? 20.0,
    keyConfigured: m.keyConfigured || false,
    veyra_max_references: m.veyra_max_references ?? 2,
    maxReferences: m.veyra_max_references ?? 2,
    supports_references: m.supports_references ?? true,
    supports_editing: m.supports_editing ?? false,
    provider_notes: m.provider_notes || "",
    supportsFLF: Boolean(m.supports_flf ?? m.modes?.includes("first_last_frame")),
    supportsMultishot: Boolean(m.supports_multishot),
    multishotStatus: m.multishot_status || "not_supported",
    // Sobrecoste por referencia adicional (NO VERIFICADO — de estimated_cost_extra_ref_usd)
    extraRefCost: m.estimated_cost_extra_ref_usd ?? null,
    // Campos de catálogo ampliado
    pendingIntegration: m.pending_integration ?? false,
    priceVerified: m.price_verified ?? Boolean((m.price_type && m.price_type !== "not_available") && (m.estimated_cost_usd || m.reserve_usd || m.price_usd != null)),
    pricePending: m.price_pending ?? false,
    generationAvailable: m.generation_available ?? (!m.pending_integration && (m.price_verified ?? false) && !(m.price_pending ?? false)),
    priceType: m.price_type ?? (m.price_verified ? "fixed" : "not_available"),
    priceSource: m.price_source ?? "",
    priceLastChecked: m.price_last_checked ?? "",
    supportsOutputFormat: m.supports_output_format ?? true,
    // Campos de audio nativo
    audioSupported: m.audio_supported ?? false,
    defaultAudio: m.default_audio ?? false,
    audioSurchargeRateUsd: m.audio_surcharge_rate_usd ?? 0.0,
    audioIncompatibleWith: m.audio_incompatible_with || [],
    // Campos de upscale
    upscaleScales: m.upscale_scales || [],
    // Límite de caracteres de prompt
    maxPromptChars: m.max_prompt_chars || (m.model_id?.startsWith("kling") ? 2500 : 5000),
    // model_kind: "image" | "video" | "upscale" — usado para filtrar por herramienta.
    modelKind: m.model_kind || m.category || m.kind || "image",
  };
}
